import { isMorphEnvelope, MORPH_TO } from '~/schemas/morph'
import gsap from 'gsap'
import { Texture } from 'pixi.js'
import display_object_schema from '~/schemas/display_object_schema'
import store from '~/store'
import { getEngine } from '~/lib/automation'
import { stringToRawPath } from '~/lib/gsap/utils/paths'
import {
	classifyParam,
	getObjectInitial,
	buildIntegerSnapConfig,
	fillVocabPins,
	isPinned,
	sourceParamPath,
	sourceParams,
	sourcePinKey,
	sourceValues,
	vocabPinValues,
} from '~/lib/params'
import { clampToRange, pathOf, pathOverlap, pathRange, pathTween, steppedMoves } from '~/lib/shaders/common'
import { produce } from 'immer'
import { fillSourcesOf, isSourceFill } from '~/lib/sourceFill'
import { clearFadeSources, showFadeSource } from '~/lib/sourceWrites'
import {
	boundParams,
	getShader,
	heldIds,
	isColorParam,
	isPathed,
	palettePath,
	rgb01ToHex,
	uniformValue,
	variantOf,
} from '~/lib/shaders'
import { selectLiveProps } from '~/store/playback/selectors'
import { applyCarry, carriedObjects } from '~/lib/livePins'
import { EFFECT_SCHEMAS } from '~/lib/presetSettings'
import { getStoredChildWorldBounds, localExtent, localPivot, resolveWorldTransform } from '~/lib/objectBounds'
import { VECTOR_TYPES } from '~/lib/flipObject'
import {
	toPixiFill,
	drawBgFill,
	getCachedTexture,
	loadImageTextureFor,
	clearMask,
	fillBacking,
	fillNodeObj,
} from '~/comps/PixiStage/usePixiObjects'
import { followFill, liveEnv } from '~/comps/PixiStage/livePose'
import {
	bindSampleTexture,
	keyShaderFieldMorph,
	setShaderFieldMorphProgress,
	settleShaderFieldMorph,
	shaderFrame,
	switchShaderVariant,
	warmShaderFieldMorph,
	writeSampleValues,
	writeShaderValues,
} from '~/comps/PixiStage/pixiShaderMesh'
import { rawPathToString } from '~/lib/svg/paths'
import { hasFrame, holdSource } from '~/lib/sources/textures'
import { isFileSource } from '~/lib/sources/ids'
import { setOverride as setPlayheadOverride } from '~/lib/sources/playhead'
import video_file_schema from '~/schemas/video_file_schema'
import { resolveAssetUrlSync } from '~/lib/resolveAssetUrl'
import { sendInpaintMaskUpdates } from '~/lib/inpaintMaskSync'
import { parseHexAlpha } from '~/lib/pixiColor'
import { prepareFillPair } from '~/lib/fillInterpolation'
import { IS_LOCAL } from '~/lib/constants'
import paramChannel from '~/lib/paramChannel'
import MorphPlugin from './gsap/MorphPlugin'
import { setMorph, clearMorph, setTransitionPaused } from '~/store/playback'

const logger = console.scope('transitionManager')

gsap.registerPlugin(MorphPlugin)

// Get initial value for an object property from its type schema. Thin wrapper over the shared accessor (getObjectInitial), kept as a local alias so the call sites below read unchanged. Returns undefined for unknown type/key.
const getInitialValue = (type, propName) => getObjectInitial(type, propName)

// Snap config ({key:1}) for one effect's integer params — derived via the shared classifier, the single home for the `type: 'integer'` fact (also used by liveSync's INTEGER_PARAMS).
const getSnapConfig = effect => buildIntegerSnapConfig(EFFECT_SCHEMAS[effect]?.properties ?? {})

// A world rotation is `child + group`, so it is unbounded: 474° renders as 114° but tweens an extra turn from 0.
const shortestRotation = (fromDeg, toDeg) => fromDeg + ((((toDeg - fromDeg) % 360) + 540) % 360) - 180

/** ⚠️ ABSENT SCHEMA ⇒ TWEENS, unlike object props: `to` is wire state, so a key the local schema lacks is most likely a param from a newer contract. */
const jumpsRatherThanTweens = (effect, key) => {
	const schema = EFFECT_SCHEMAS[effect]?.properties?.[key]
	if (!schema || schema.morph) return false
	return !classifyParam(schema).tweenable
}

/** Every effect a preset fade animates, derived from the one map every direction iterates (presetSettings). `objects` is deliberately absent there — it is the scene graph, handled separately as per-Pixi-object tweens. */
const TWEEN_EFFECTS = Object.keys(EFFECT_SCHEMAS)

// A file's params a same-file fade blends; the rest of its loop changes at the settle.
const FILE_TWEEN_KEYS = ['rate', 'volume']

// A fill crossfade whose destination picture has no frame by then cuts in.
const CUT_IN_MS = 3000

const fillSourceOf = obj => (isSourceFill(obj?.fill) ? obj.fill.source : null)

// A keyed fill morph bakes its outline this often; the GPU blends between keys.
const FIELD_KEY_MS = 100

// Live objects with one shape where its linear morph puts it.
class WithObject extends Map {
	constructor(base, id, obj) {
		super()
		this.base = base
		this.at = id
		this.obj = obj
	}

	get(id) {
		return id === this.at ? this.obj : this.base.get(id)
	}

	has(id) {
		return id === this.at || this.base.has(id)
	}
}

const MORPH_POSE = ['x', 'y', 'rotation', 'scaleX', 'scaleY']

const copyPath = raw => raw.map(seg => Object.assign(seg.slice(), { closed: seg.closed }))

const samePathShape = (a, b) => a.length === b.length && a.every((seg, i) => seg.length === b[i].length)

const dropOld = ({ node, release }) => {
	window.pixi_drop_fill_node?.(node)
	release?.()
}

// A handed-over dissolve nothing continues: its incoming node shows in full, its outgoing ones go.
const dropFillCarry = ({ next, olds }) => {
	if (next && !next.destroyed) next._fade = undefined
	for (const o of olds) dropOld(o)
}

// A colour or gradient with every alpha at 0: the end a shape's own fill fades through while a source fill takes over.
const clearOf = fill => {
	const clear = c => `${typeof c === 'string' && c.length >= 7 ? c.slice(0, 7) : '#000000'}00`
	return fill?.stops?.length
		? { ...fill, stops: fill.stops.map(s => ({ ...s, color: clear(s.color) })) }
		: clear(fill)
}

// The shape-fill pair a fade lerps when a source fill is on one end: the other end through clear. Null when both ends are sources.
const shapeFillPair = (from, to) => {
	const fromSource = isSourceFill(from)
	const toSource = isSourceFill(to)
	if (fromSource && toSource) return null
	return prepareFillPair(fromSource ? clearOf(to) : from, toSource ? clearOf(from) : to)
}

// Carried morphs: qualified → the dying morph's destination.
const EMPTY_MAP = new Map()

/** ⚠️ `t` MUST STAY LINEAR even if the engine's shared ease stops being `none`: it is progress handed to the server, whose blend is already uniform in `t`, and an ease curves a blend that did not ask for one. context/morph-params.md */
const morphEnvelope = (b, t, fade) => ({ [MORPH_TO]: b, t, fade })

/**
 * The REDUX projection of a morph. `from` is UI-ONLY and never reaches the wire — an explicit `a` there would assert an anchor the client cannot know (see above). `fade` is the mirror case: a wire-only re-latch key with nothing to render, so this shape omits it.
 *
 * ⚠️ Empty when the fade DETACHED: its origin is a mid-fade blend no value names, so the outgoing frame renders blank. Mirrors `selectTransitionBar` reporting `from: -1`.
 */
const morphDisplay = (b, t, from) => ({ [MORPH_TO]: b, t, from: from ?? '' })

// Keys that transition via a bespoke strategy below (NOT the generic numeric tween, and NOT caught by classifyParam().tweenable because they are non-numeric): the path/brush morph (`data`), colour/gradient interpolation (`fill`), and the stroke-colour alias (`stroke`). The per-key transition guard lets these through in addition to tweenable numeric params. (`rotation` and the TRANSFORM_PROPS are ordinary continuous params → already tweenable.)
const SPECIAL_TWEEN_KEYS = new Set(['data', 'fill', 'stroke'])

// One formula per source-param kind, shared by the mesh write and the `getActiveState` snapshot. An enum selects a GLSL branch, so it crosses once at the midpoint; a `wrap` param takes the literal numeric path, so 350°→10° sweeps through 180°.
const lerpSourceParam = (e, t) => {
	if (e.mirror) return e.to
	if (e.step) return t < 0.5 ? e.from : e.to
	return e.from + (e.to - e.from) * t
}
const lerpColorChannel = (e, i, t) => e.from[i] + (e.to[i] - e.from[i]) * t

// Colours, numbers and vectors (per component) blend; a mirror lands at frame one under a still of the FROM picture; other booleans cut at the settle.
const sourceEntries = (source, from, to, liveProps) => {
	const file = isFileSource(source)
	const entries = []
	for (const [key, def] of Object.entries(sourceParams(source) ?? {})) {
		if (file && key in video_file_schema && !FILE_TWEEN_KEYS.includes(key)) continue
		if (isPinned(liveProps, sourceParamPath(source, key))) continue
		const a = from[key] ?? def.initial
		const b = to[key] ?? def.initial
		if (JSON.stringify(a) === JSON.stringify(b)) continue
		if (isColorParam(def)) entries.push({ key, color: true, from: uniformValue(a, def), to: uniformValue(b, def) })
		else if (key === 'mirror') entries.push({ key, mirror: true, from: a, to: b })
		else if (Array.isArray(a) || Array.isArray(b)) {
			// A scalar end of a vec2 (equal axes) spreads over the other end's length.
			const n = Math.max([a].flat().length, [b].flat().length)
			const vec = v => Array.from({ length: n }, (_, i) => [v].flat()[i] ?? [v].flat()[0])
			entries.push({ key, vec: true, from: vec(a), to: vec(b) })
		} else if (typeof a === 'number' && typeof b === 'number')
			entries.push({
				key,
				from: a,
				to: b,
				step: classifyParam(def).discrete,
			})
	}
	return entries
}

// The colour pair's hue route, as a path fill takes it (`palettePath`).
const sourcePalette = (source, from, entries) => {
	const ends = key => {
		const e = entries.find(x => x.key === key)
		const v = from[key] ?? sourceParams(source)?.[key]?.initial
		return e ? [rgb01ToHex(e.from), rgb01ToHex(e.to)] : [v, v]
	}
	return palettePath(...ends('color'), ...ends('color_b'))
}

const blendSource = ({ from, entries, palette }, t) => ({ ...from, ...blendPath(entries, t), ...palette?.(t) })

const blendPath = (entries, t) => {
	const values = {}
	for (const e of entries)
		values[e.key] = e.color
			? rgb01ToHex(e.from.map((_, i) => lerpColorChannel(e, i, t)))
			: e.vec
				? e.from.map((v, i) => v + (e.to[i] - v) * t)
				: lerpSourceParam(e, t)
	return values
}

const sameIds = (x, y) => x.length === y.length && x.every(id => y.includes(id))

// A path's node onto one end: that subset's own params and bag, the clock carried.
const settlePath = (r, atEnd) => {
	const [shader, source, bag] = atEnd ? [r.b, r.source, { ...r.toBag, ...r.edits }] : [r.a, r.aSource, r.fromBag]
	r.node._path = null
	r.node._fillSource = source
	if (!switchShaderVariant(r.node, shader, variantOf(shader), bag)) writeShaderValues(r.node, bag)
}

// Paths an interrupted fade carried that no successor took over (`taker` the one now building) land on their B.
const settleCarriedPaths = taker => {
	for (const [, { fill }] of window.pixi_objects_map ?? [])
		if (fill?._path?.carried && fill._path.child !== taker && !fill.destroyed) settlePath(fill._path, true)
}

// Properties that need special transform handling
const TRANSFORM_PROPS = new Set(['x', 'y', 'rotation', 'scaleX', 'scaleY'])

// Shape keys a visibility crossing holds alongside TRANSFORM_PROPS: they resize or redraw the object, and on a crossing the far endpoint belongs to the side that was never rendered.
const CROSSING_SHAPE_PROPS = new Set(['width', 'height', 'data', 'strokeWidth', 'fill', 'stroke'])

// The types whose `scaleX`/`scaleY` the schema declares and that render through `pixiObj.scale` rather than a `PixiShape` alias: the generic per-key tween must never touch those two keys.
const SCALE_REALIZED_TYPES = new Set(['image', 'group'])

// `classifyParam` cannot answer this: it sees one key's schema, where `scaleX` is an ordinary tweenable number. Whether a tween is possible depends on the PAIR — 1→2 interpolates, 1→−1 has no path that is not a collapse or a pop.
const signOf = v => Math.sign(v ?? 1) || 1
// A fill's still keeps its FROM box relative to its shape's: a field morph's own quad steps once per key.
const boxOf = b => ({
	x: b.x ?? b.minX,
	y: b.y ?? b.minY,
	w: b.width ?? b.maxX - b.minX,
	h: b.height ?? b.maxY - b.minY,
})
const followRatio = (box, anchorBox) => {
	const g = boxOf(box)
	const a = boxOf(anchorBox)
	if (!a.w || !a.h) return null
	return { x: (g.x - a.x) / a.w, y: (g.y - a.y) / a.h, w: g.w / a.w, h: g.h / a.h }
}
const followBox = (r, anchorBox) => {
	const a = boxOf(anchorBox)
	return { x: a.x + r.x * a.w, y: a.y + r.y * a.h, width: r.w * a.w, height: r.h * a.h }
}
const mirrorFlipped = (fromObj, toObj) =>
	signOf(fromObj?.scaleX) !== signOf(toObj?.scaleX) || signOf(fromObj?.scaleY) !== signOf(toObj?.scaleY)

/** A vector's own mirror on `key` folds into its outline morph: the start path is mirrored about the pivot under the destination sign, so the outline holds at frame one. */
const foldsMirror = (id, fromObj, toObj, key, liveProps) =>
	VECTOR_TYPES.has(toObj?.type) &&
	toObj.type !== 'brush' &&
	fromObj?.type === toObj.type &&
	!isPinned(liveProps, `objects.${id}.${key}`) &&
	signOf(fromObj[key]) !== signOf(toObj[key])

const mirrorPath = (raw, pivot, axes) =>
	raw.map(seg =>
		Object.assign(
			seg.map((v, i) => (i % 2 ? (axes.y ? 2 * pivot.y - v : v) : axes.x ? 2 * pivot.x - v : v)),
			{ closed: seg.closed }
		)
	)

/** A reorder is carried by stills of the objects whose picture holds, so a morphing outline stays live; a partner ending above it dissolves in over its own still. */
const depthHandOff = (fromMap, toMap, stencils, liveProps) => {
	const live = new Set()
	const rises = new Set()
	const survives = list => list.filter(o => fromMap.has(o) && toMap.has(o))
	const a = survives(fromMap.get('root')?.children ?? [])
	const b = survives(toMap.get('root')?.children ?? [])
	const shown = id =>
		isShownIn(fromMap.get(id), fromMap, stencils.from) && isShownIn(toMap.get(id), toMap, stencils.to)
	const holds = id => {
		const f = fromMap.get(id)
		const t = toMap.get(id)
		return (
			f.type === t.type &&
			t.type !== 'group' &&
			f.data === t.data &&
			!mirrorFlipped(f, t) &&
			(f.mask ?? null) === (t.mask ?? null) &&
			fillSourceOf(f) === fillSourceOf(t) &&
			(f.source ?? null) === (t.source ?? null)
		)
	}
	const morphs = id => {
		const f = fromMap.get(id)
		const t = toMap.get(id)
		if (!VECTOR_TYPES.has(t.type) || f.type !== t.type || f.data === t.data) return false
		if ((f.mask ?? null) !== (t.mask ?? null)) return false
		return ['scaleX', 'scaleY'].every(k => signOf(f[k]) === signOf(t[k]) || foldsMirror(id, f, t, k, liveProps))
	}
	for (const m of b) {
		if (!depthMoved(m, fromMap, toMap) || !shown(m) || !morphs(m)) continue
		const partners = b.filter(
			p => p !== m && shown(p) && a.indexOf(p) < a.indexOf(m) !== b.indexOf(p) < b.indexOf(m)
		)
		if (!partners.every(p => holds(p) && depthMoved(p, fromMap, toMap))) continue
		live.add(m)
		for (const p of partners) if (b.indexOf(p) > b.indexOf(m)) rises.add(p)
	}
	return { live, rises }
}
// The same source on both ends with its bag's mirror changing: the picture flips with no geometry to tween.
const sourceMirrorFlipped = (fromObj, toObj, fromState, toState, liveProps) => {
	const source = fillSourceOf(toObj)
	if (!source || source !== fillSourceOf(fromObj) || isPinned(liveProps, sourceParamPath(source, 'mirror')))
		return false
	return !!sourceValues(fromState, source).mirror !== !!sourceValues(toState, source).mirror
}

// Survivors only: a slot moving under a fading object is invisible.
//
// ⚠️ BOTH indices must be real: -1 means "not a root child", and comparing it as a position reads a group-boundary reparent as a depth change — on the leaving direction only, which is where the surplus ghost came from. The crosser path in `_buildObjectTweens` owns reparents.
const depthMoved = (id, fromMap, toMap) => {
	const survives = other => other.filter(o => fromMap.has(o) && toMap.has(o))
	const a = survives(fromMap.get('root')?.children ?? [])
	const b = survives(toMap.get('root')?.children ?? [])
	const i = a.indexOf(id)
	const j = b.indexOf(id)
	return i >= 0 && j >= 0 && i !== j
}

// ⚠️ An inverse mask is restored through `setMask`: a bare `mask =` re-links it as a plain one.
const unmaskedBounds = pixiObj => {
	const mask = pixiObj.mask
	if (!mask) return pixiObj.getBounds()
	pixiObj.mask = null
	const box = pixiObj.getBounds()
	if (pixiObj._maskInverse) pixiObj.setMask({ mask, inverse: true })
	else pixiObj.mask = mask
	return box
}

// PixiShape exposes an `opacity` alias (0–100) that writes through to native `alpha`; a plain Sprite (image) does not. Fade in/out must tween whichever the object actually supports — tweening `opacity` on a Sprite would mutate an inert expando and never touch `alpha`. (Deferred: migrate all objects off the 0–100 `opacity` alias to native `alpha`.)
const hasOpacityAlias = pixiObj => 'opacity' in pixiObj

// One per-frame step of a fade, isolated so a throw skips only itself; each logs once per fade, not every frame.
const runStep = (failed, name, fn) => {
	try {
		fn()
	} catch (e) {
		if (failed.has(name)) return
		failed.add(name)
		logger.error(`fade frame step '${name}' failed`, e)
	}
}

// One write through `updatePixiObject` with React's gate lowered, restored whatever the write does.
const ungated = fn => {
	const was = window.pixi_transition_active
	window.pixi_transition_active = false
	try {
		return fn()
	} finally {
		window.pixi_transition_active = was
	}
}

// Per-object scratch a fade hangs on the pixiObj itself rather than in a registry — there is no id list to walk, so the sweep is over every mapped object.
const clearPixiTransitionProxies = () => {
	if (!window.pixi_objects_map) return
	for (const [, entry] of window.pixi_objects_map) {
		if (!entry.pixiObj) continue
		entry.pixiObj._brushMorphParams = null
		entry.pixiObj._fillTransitionProxy = null
		entry.pixiObj._scaleTransitionProxy = null
	}
}

// The settle registry only undoes what a fade ADDED; React's full sync pass — reached through the `pixi_sync_scene` seam, which lowers the gate for one pass — restores what it MOVED (parenting, transforms, z-order, masks) and creates/destroys what Redux renamed.
const resyncPixiFromLive = () => {
	const objects = store.getState().live?.objects
	if (objects) window.pixi_sync_scene?.(objects)
}

// Write alias-aware fade props (Redux 0–100 scale) into a tween-vars object. Every opacity tween must go through here: an `image` is a plain Sprite with no `opacity` alias, so a raw `tweenProps.opacity` write lands on an inert expando and the object never fades.
export const setOpacityVars = (vars, pixiObj, opacity0to100) => {
	if (hasOpacityAlias(pixiObj)) vars.opacity = opacity0to100
	else vars.alpha = opacity0to100 / 100
	return vars
}

// Set current fade level on the Redux 0–100 scale, alias-aware.
export const setOpacity = (pixiObj, opacity0to100) => {
	if (hasOpacityAlias(pixiObj)) pixiObj.opacity = opacity0to100
	else pixiObj.alpha = opacity0to100 / 100
}

// `visible` is a boolean → non-tweenable by the classifier, so a true⇄false flip would pop. Treat it like existence: a hidden object is equivalent to an absent one, so crossing the boundary fades via opacity (holding `visible=true` for the duration) and settles on the discrete value at the end. Object stays `visible=false` — and therefore free — whenever it is hidden on BOTH sides, matching the cost profile of an object absent from both presets.
export const isShown = obj => obj?.visible !== false && !obj?.inpaint_mask

/**
 * A vector's inherited mirror lives on the group Container and React re-applies it there on reparent, so composing it onto the object too renders it mirrored twice. Strip the sign and move the position so the unmirrored box covers the mirrored one, which is what the object renders at once reparented.
 *
 * A mirror turns the box about the pivot, so the shift is `2·pivot − (near + far)` of the extent — zero at a centred pivot. Mutates `pose`; mirrors the crosser's re-anchoring in `_buildObjectTweens`.
 */
const applyInheritedMirror = (pose, obj, map) => {
	if (map.get?.(obj.parentId)?.type !== 'group') return
	const rot = (pose.rotation * Math.PI) / 180
	const cos = Math.cos(rot)
	const sin = Math.sin(rot)
	const e = localExtent(obj)
	const p = localPivot(obj, null, e)
	if (pose.scaleX < 0) {
		const d = (2 * p.x - 2 * e.x - e.width) * Math.abs(pose.scaleX)
		pose.x += d * cos
		pose.y += d * sin
		pose.scaleX = Math.abs(pose.scaleX)
	}
	if (pose.scaleY < 0) {
		const d = (2 * p.y - 2 * e.y - e.height) * Math.abs(pose.scaleY)
		pose.x -= d * sin
		pose.y += d * cos
		pose.scaleY = Math.abs(pose.scaleY)
	}
}

/** A vector's own scale is its draw scale, so a world pose written to `pixiObj.scale` composes only what it inherits. */
const inheritedPose = obj => (VECTOR_TYPES.has(obj?.type) ? { ...obj, scaleX: 1, scaleY: 1 } : obj)

// Effective visibility: PixiJS inherits `visible` down the scene graph, so an object is only really on screen when it AND every ancestor is shown. A group⇄root crosser can keep `visible: true` on both sides while its destination parent is hidden — the object then has no crossing by its own flag, never fades, and simply disappears when React reparents it into the hidden group at completion. Walk the chain so that reads as the crossing it is.
export const isShownIn = (obj, objects, stencils = null, depth = 0) => {
	if (!isShown(obj)) return false
	// Stencil geometry is consumed by whatever it masks and never painted — the same ground as `inpaint_mask` above. `stencils` holds ids under a `mask` link, so a MASKED object stays shown.
	if (stencils?.has(obj?.id)) return false
	const parentId = obj?.parentId
	if (!parentId || parentId === 'root' || depth > 16) return true
	const parent = objects instanceof Map ? objects.get(parentId) : objects?.[parentId]
	return parent ? isShownIn(parent, objects, stencils, depth + 1) : true
}

/** Add `rootId` and, when it is a group, every descendant — the drawables `_buildMaskContainer` clones. */
const addSubtree = (set, rootId, map, depth = 0) => {
	if (depth > 16 || set.has(rootId)) return
	set.add(rootId)
	const obj = map instanceof Map ? map.get(rootId) : map?.[rootId]
	if (obj?.type === 'group') for (const c of obj.children ?? []) addSubtree(set, c, map, depth + 1)
}

/** Every id serving as stencil geometry in one state. */
export const maskMembers = map => {
	const out = new Set()
	const entries = map instanceof Map ? map.values() : Object.values(map ?? {})
	for (const obj of entries) if (obj?.mask) addSubtree(out, obj.mask, map)
	return out
}

/**
 * Everything that belongs to ONE fade — membership is exactly what `kill()` nulls.
 *
 * ⚠️ Field names match the manager's own, because the manager delegates to the child at the playhead. A rename breaks that delegation silently.
 */
export const makeFadeChild = tl => ({
	kind: 'fade',
	tl,
	// One slot per effect, `diffusion` included. The proxy objects GSAP tweens and `getActiveState()` reads to snapshot a fade mid-flight.
	_effectTargets: null,
	// Progress proxies for the morphing params, keyed by qualified name. GSAP animates each `t`; the server owns the interpolation between the endpoints.
	_morphProxies: null,
	_fromState: null,
	_toState: null,
	_visibilityCrossings: null,
	_maskCrossings: null,
	_maskStencilFades: null,
	_vectorScaleRestores: null,
	_ghostCrossfades: null,
	_mirrorPositions: null,
	// Objects this fade created or is fading out, keyed by id — resolved against whatever state comes next.
	_owned: null,
	// Root background fill progress, read by `getActiveState()` to snapshot a fade mid-flight.
	_bgFillProxy: null,
	// `{ proxy, sources: { <source>: { from, entries } } }`: the bags this fade blends, read by `getActiveState()`.
	_sourceBlend: null,
	// Path fades (`_pathFill`), read by `getActiveState()` and settled per node.
	_pathFills: null,
	// `{name, apply}` per thing this fade put on the stage, in the order it must be undone.
	_settlers: null,
	// The ONE-WAY settle gate: teardown is destructive and not idempotent, but GSAP fires it on EVERY crossing of `duration`. Only a deliberate finish opens it.
	_settling: false,
})

// The fields the manager delegates to the child at the playhead: everything a fade owns, nothing the run owns.
const CHILD_FIELDS = Object.keys(makeFadeChild(null)).filter(k => k.startsWith('_'))

class TransitionManager {
	constructor() {
		// The autoplay run's timeline, whose children are the fades and holds. NULL for a standalone fade — the degenerate plan of one child on no parent, which is every manual transition.
		this._parent = null
		// The run's children in playhead order, and which one the playhead is on. A standalone fade is one child at index 0.
		this._children = []
		this._at = 0
		// The envelope's `fade` re-latch key, bumped once per `createTransition`. MONOTONIC — deliberately not reset by `kill()`: a reused id is the collision the key exists to prevent.
		this._fadeId = 0
		// Ownership a killed fade handed to the successor, held between `kill()` and the `createTransition` that adopts it.
		this._carried = null
		// Unfinished fill dissolves a carry handed over, by shape id, held like `_carried`.
		this._carriedFills = null
		// The child a boundary just settled, held for the successor's `buildFade` to read its morphs off. `_at` has already moved by then.
		this._dying = null
		// A run an interrupt took the stage from: its parent (paused, with the playhead's child removed) and the remaining children. Held so the interrupt's landing can re-plan behind it rather than guess what came next.
		this._parked = null
		// The running plan's `onSettle`/`onEnd`, kept so a child a gesture detaches can still settle when it lands.
		this._runCallbacks = null
	}

	// The record at the playhead, or null.
	_child() {
		return this._children[this._at] ?? null
	}

	/**
	 * The fade at the playhead.
	 *
	 * ⚠️ A getter because THREE external readers depend on it: `thunks.js` takes its `progress()`, `rig.js` compares it by IDENTITY to prove a new transition started, and it is a documented console handle.
	 */
	get timeline() {
		const c = this._child()
		return c?.kind === 'fade' ? c.tl : null
	}

	// Assigning null ends the run; assigning a timeline opens a standalone fade. Both spellings already exist in `kill()` and `createTransition`.
	set timeline(tl) {
		// A standalone fade is parentless by definition; a run installs its parent through `_setPlan`.
		this._parent = null
		if (!tl) {
			this._children = []
			this._at = 0
			return
		}
		this._children = [makeFadeChild(tl)]
		this._at = 0
	}

	/**
	 * Install an autoplay run: the parent timeline and its children in playhead order.
	 *
	 * ⚠️ Call BEFORE the fade child's own state is written — the per-fade fields delegate to `_children[_at]`.
	 */
	_setPlan(parent, children, at = 0) {
		this._parent = parent
		this._children = children
		this._at = at
	}

	/**
	 * Build and start the autoplay run: one parent timeline whose children are this plan's fades and holds.
	 *
	 * Children are added at explicit numeric positions accumulated from the step durations — never `'>'`, which resolves against the parent's current end and is silently fragile once a tail is removed.
	 *
	 * ⚠️ A fade child is added EMPTY, reserving only its duration; its scene work runs at its own `onStart`, never at plan time.
	 *
	 * @param steps    from `planRun`.
	 * @param onFade   `(step, child) => void` — build this fade's tweens into `child.tl`.
	 * @param onHold   `(step, child) => void` — the hold has begun.
	 * @param onEnd    the run reached its end; the caller decides whether to re-plan or stop.
	 */
	startRun(steps, { onFade, onHold, onSettle, onEnd } = {}) {
		this.kill()
		// ⚠️ Held on the manager, not only in this closure: `_handOffChild` re-arms a DETACHED child from here, and `replanTail` wires fresh children long after this closure is gone.
		this._runCallbacks = { onFade, onHold, onSettle, onEnd }
		const parent = gsap.timeline({ paused: true })
		const children = []
		let at = 0
		for (const step of steps) {
			children.push(this._addChild(parent, step, at))
			at += step.ms / 1000
		}

		this._setPlan(parent, children, 0)
		children.forEach((child, i) => this._wireChild(child, i))

		parent.eventCallback('onComplete', () => {
			const dying = this._children[this._at]
			if (dying) this._settleChild(dying, -1, onSettle)
			onEnd?.()
		})
		parent.play()
		return parent
	}

	/**
	 * One child of the run, reserving its duration on `parent` at an explicit numeric position.
	 *
	 * ⚠️ Positions are passed in, never `'>'`: it resolves against the parent's current end and is silently wrong once a tail has been removed.
	 */
	_addChild(parent, step, at) {
		const tl = gsap.timeline()
		// Reserve the child's length with no work in it. ⚠️ Every child has a span — the planner emits none without one, because GSAP never positions an empty child and its `onStart` would never fire.
		const spacer = tl.set({}, {}, step.ms / 1000)
		const child = step.kind === 'fade' ? makeFadeChild(tl) : { kind: step.kind, tl, step }
		// The reservation is RE-SIZED by `retime` while it is still empty, so the tween that holds it open must stay reachable. Cleared once the fade builds into it — see `buildFade`.
		child._spacer = spacer
		child.step = step
		parent.add(tl, at)
		return child
	}

	/** The boundary handler for one child, by INDEX rather than identity: a tail re-plan replaces `_children`, and a closure over the record would settle against one no longer in the plan. */
	_wireChild(child, i) {
		child.tl.eventCallback('onStart', () => {
			const { onFade, onHold, onSettle } = this._runCallbacks ?? {}
			// ⚠️ ATOMIC, and in this order. The outgoing child settles, the playhead advances, then the new child builds — with no dispatch in between that could reentrantly land a `loadPreset` on a child whose registries were just nulled.
			//
			// A's onComplete and B's onStart share one synchronous tick at the same playhead position (measured), so nothing renders between them: this is the settle-collapse the whole design rests on.
			const dying = this._children[this._at]
			if (dying && dying !== child) this._settleChild(dying, i, onSettle)
			// Every builder reached below resolves `_child()` to THIS child, not to the one just settled.
			this._at = i
			if (child.kind === 'fade') onFade?.(child.step, child)
			else onHold?.(child.step, child)
		})
	}

	/**
	 * How long a child occupies of its PARENT's clock.
	 *
	 * ⚠️ Not `duration()`, which is local: `retime` re-rates a fade rather than rebuilding it, so a re-rated child reports a local length its parent span no longer matches.
	 */
	_span(child) {
		return child.tl.duration() / (child.tl.timeScale() || 1)
	}

	/**
	 * Change the SPEED the playhead moves through the plan — a `hold_duration` or `fade_duration` edit. Takes the same route as the running plan, differing only in durations; false when no run is playing or the route differs.
	 *
	 * ⚠️ The child's OWN elapsed time carries over, converted back through its scale. Re-rating leaves a child's internal position untouched, so its old parent-time offset denotes a different point in it and the fade jumps backwards.
	 *
	 * ⚠️ CLAMPED to the child's new span, which also keeps the seek from landing before its start: shortening a hold below the elapsed time would seek PAST it and skip the next child, and a seek behind a child re-fires `onStart`.
	 */
	retime(steps) {
		if (!this._parent) return false
		const current = this._children[this._at]
		if (!current) return false
		// A different route is a rebuild: re-positioning against a mismatched list leaves stale steps on the children it never reached.
		if (steps.length !== this._children.length) return false

		const elapsed = current.tl.time()

		let at = 0
		const starts = this._children.map((child, i) => {
			const start = at
			at += steps[i].ms / 1000
			return start
		})

		this._children.forEach((child, i) => {
			// An EMPTY reservation is re-sized outright: its scene work is built later, at its own `onStart`, spanning whatever local length it finds — so a scaled reservation would hand the builder the OLD length and the fade would overrun the span the parent reserved. Only a child already holding tweens is re-rated.
			//
			// ⚠️ Against the timeline's OWN unchanging length, never `child.step.ms` — the rate must be absolute, or each pass re-rates the previous rate away.
			if (steps[i].ms) {
				if (this._isReservation(child, i)) this._resizeReservation(child, steps[i].ms / 1000)
				else child.tl.timeScale(child.tl.duration() / (steps[i].ms / 1000))
			}
			child.step = steps[i]
			this._parent.remove(child.tl)
			this._parent.add(child.tl, starts[i])
		})

		this._parent.time(
			starts[this._at] + Math.min(elapsed / (current.tl.timeScale() || 1), steps[this._at].ms / 1000)
		)
		return true
	}

	/**
	 * Whether this child is still an empty reservation — the spacer and nothing else.
	 *
	 * ⚠️ Asked of the TIMELINE, never of a flag alone: `clear()` below destroys whatever the child holds, so anything that fills a child without clearing the flag would have its work wiped mid-fade. The spacer standing alone is what makes the answer un-forgeable.
	 */
	_isReservation(child, i) {
		// ⚠️ NEVER the child at the playhead, whatever it holds: re-seating its spacer moves a boundary the playhead has already crossed, and a seek behind a child re-fires `onStart` — running its builder a second time. That child is re-rated like any other in flight; only what the playhead has yet to reach can be re-sized.
		if (i <= this._at) return false
		return !!child._spacer && child.tl.getChildren(false, true, true).length === 1
	}

	/**
	 * Re-length a child that is still an EMPTY reservation, so the builder that later fills it spans the duration the plan now names.
	 *
	 * ⚠️ `clear()` and a fresh spacer, which is the only thing that re-lengths the timeline itself. Measured: moving the spacer's `startTime` leaves `duration()` at the old length (a timeline caches it and a child's move does not invalidate it), and the `duration()`/`totalDuration()` setters re-rate through `timeScale` rather than re-length — the very scale this exists to avoid.
	 */
	_resizeReservation(child, seconds) {
		child.tl.clear()
		child.tl.timeScale(1)
		child._spacer = child.tl.set({}, {}, seconds)
	}

	/**
	 * Rebuild the plan behind the playhead's child, the run continuing: a preset reorder, delete or edit.
	 *
	 * ⚠️ The child at the playhead is never rebuilt or killed: `kill()` drains its settle registry and destroys objects mid-fade. Re-TIMING it is safe — that is `retime`.
	 *
	 * @param steps the WHOLE new plan; only the part after the current child is used.
	 * @returns false when there is no run to re-plan, so the caller can start one cold.
	 */
	replanTail(steps) {
		if (!this._parent) return false
		const current = this._children[this._at]
		if (!current) return false

		// Removal alone would leave them on the global timeline, still ticking toward scenes nothing will build.
		for (let i = this._at + 1; i < this._children.length; i++) {
			const tl = this._children[i].tl
			this._parent.remove(tl)
			tl.kill()
		}

		let at = current.tl.startTime() + this._span(current)
		const kept = this._children.slice(0, this._at + 1)
		for (const step of steps.slice(this._at + 1)) {
			kept.push(this._addChild(this._parent, step, at))
			at += step.ms / 1000
		}

		this._children = kept
		// Only the new tail needs wiring: the kept children's handlers close over indices that are still correct.
		for (let i = this._at + 1; i < kept.length; i++) this._wireChild(kept[i], i)
		return true
	}

	/**
	 * Settle the child the playhead is leaving.
	 *
	 * ⚠️ `handingOver` is what keeps `pixi_transition_active` PARENT-scoped: `clearPixiTransitionProxies` sweeps `window.pixi_objects_map` globally, so it may only run when no successor child is opening. The planner already knows this — it is "is the next child a fade with no hold before it".
	 */
	_settleChild(child, nextIndex, onSettle) {
		const next = nextIndex >= 0 ? this._children[nextIndex] : null
		const handingOver = next?.kind === 'fade'
		if (child.kind === 'fade') {
			// Read BEFORE the settle tears the proxies down; the successor's `buildFade` adopts both in this same tick.
			this._dying = child
			child.tl.kill()
			// ⚠️ `carry` follows `handingOver`: an object still fading in stays OWNED for the successor, which resolves it against its own destination — handing it to React gets it destroyed mid-fade.
			const owns = handingOver ? null : (child._toState?.objects ?? null)
			this._settleScene(child, { owns, successor: owns, carry: handingOver, handingOver })
			this._carried = handingOver ? child._owned : null
			onSettle?.(child.step, child, handingOver)
			// Reconcile inside the tick so the successor builds against its fromState — never with a live carry, whose objects the stale pass would destroy.
			if (handingOver && !this._carried?.size) resyncPixiFromLive()
		}
	}

	/**
	 * Build one PLANNED fade into the child the parent already owns, at its own `onStart`.
	 *
	 * The run's counterpart to `createTransition`: no `kill()` and no new timeline, because the outgoing child was already settled by `_settleChild` in this same tick and this child's duration is already reserved on the parent.
	 */
	buildFade(child, fromState, toState, duration, liveProps = []) {
		// ⚠️ The DYING child explicitly. `_at` has already advanced to this one, so a playhead lookup would read the empty registry of the fade being built rather than the morphs the settled fade is handing over.
		const carriedMorphs = this._carriedMorphs(this._dying)
		this._dying = null
		const carriedOwned = this._carried
		this._carried = null
		// ⚠️ Bumped per FADE, at its own onStart — never at plan time, or N children pre-allocate N ids and the envelope's re-latch key collides with itself.
		this._fadeId++
		// No longer an empty reservation: `retime` re-rates it from here on, as it does any child holding tweens.
		child._spacer = null
		this._build(child, fromState, toState, duration, liveProps, carriedMorphs, carriedOwned)
	}

	/**
	 * Detach a child from the parent and re-home it on the global timeline at the position it already holds.
	 *
	 * ⚠️ `remove()` alone ORPHANS it: the child then reports `paused() === false` while nothing ticks it, so a `reverse()` or `play()` on it silently freezes. GSAP drives a timeline only through whatever contains it, and `remove` leaves it in nothing. Re-adding it to the global timeline at `globalTime - child.time()` is what gives it a clock again without moving it.
	 *
	 * Both measured, in `transitionHandoff.test.js`.
	 */
	_detachChild(child) {
		const tl = child?.tl
		if (!tl) return
		this._parent?.remove(tl)
		gsap.globalTimeline.add(tl, gsap.globalTimeline.time() - tl.time())
	}

	/**
	 * Park the run so an interrupt can own the stage — context/autoplay.md.
	 *
	 * ⚠️ The child at the playhead is `remove()`d, never left on the parent: a resumed parent would re-enter it, re-firing `onStart` and running every builder a second time.
	 *
	 * @returns true when a run was parked.
	 */
	parkRun() {
		if (!this._parent) return false
		this._handOffChild()
		const child = this._child()
		if (child?.kind === 'fade') {
			// Handing over: the successor `createTransition` is about to resolve these against its own destination, so an object still fading in must stay OWNED rather than go back to React and be destroyed mid-fade — the same carry a run's own boundary makes.
			this._dying = child
			child.tl.kill()
			this._settleScene(child, { owns: null, successor: null, carry: true, handingOver: true })
			this._carried = child._owned
		}
		// The child is settled; `createTransition` opens its own on a manager with no children at all.
		this._children = []
		this._at = 0
		return true
	}

	/** Discard a parked plan and its parent timeline. The scene work was already settled by `parkRun`; only the clock is left to stop. */
	dropParked() {
		this._parked?.parent?.kill()
		this._parked = null
	}

	/** Whether a plan is what is running, as opposed to a standalone fade. */
	isRun() {
		return !!this._parent
	}

	/**
	 * End the run without touching a standalone fade. Project switch, `stopPlayback`, or a plan that no longer describes reality.
	 *
	 * ⚠️ A PARKED plan counts as a run: its parent is paused rather than dead, so an interrupt in flight over it would otherwise strand a live timeline nothing can reach.
	 */
	killRun() {
		this.dropParked()
		if (!this._parent) return
		this.kill()
	}

	/**
	 * What the playhead is on, as the pie's ONE read: `{kind, progress, index}`, or null at rest.
	 *
	 * ⚠️ `progress` is the CHILD's, not the parent's. The parent's spans the whole run, and a pie drawn against it would creep across minutes instead of filling per step.
	 */
	activeChild() {
		const c = this._child()
		if (!c) return null
		return { kind: c.kind, progress: c.tl?.progress() ?? 0, index: this._at }
	}

	// The clock a transport gesture acts on. ⚠️ A child of a paused parent reports `paused() === false`, so pause state must be read and written HERE, never on the child.
	_clock() {
		return this._parent ?? this.timeline
	}

	/**
	 * Hand the fade at the playhead to the user: park the run and detach the child so the gesture drives a clock of its own.
	 *
	 * The parked parent is held only so `dropParked` can kill it; its remaining steps are never resumed. Whatever the gesture ends in re-plans COLD through `startRun`, from wherever the fade landed.
	 *
	 * A no-op with no parent, which is every standalone fade: there the child already sits on the global timeline and the gesture drives it directly.
	 */
	_handOffChild() {
		if (!this._parent) return
		this._parent.pause()
		const child = this._child()
		if (child) this._detachChild(child)
		this._parked = { parent: this._parent, children: this._children, at: this._at }
		this._parent = null
		this._children = child ? [child] : []
		this._at = 0
		if (child?.kind === 'fade') this._armDetachedSettle(child)
	}

	/**
	 * ⚠️ A PLAN child carries no `onComplete` of its own — the parent's `onStart` chain settles it. Detached, that chain is gone, so without this the fade plays to 1 settling NOTHING and autoplay stalls.
	 */
	_armDetachedSettle(child) {
		const { onSettle, onEnd } = this._runCallbacks ?? {}
		if (!onSettle && !onEnd) return
		child.tl.eventCallback('onComplete', () => {
			child.tl.eventCallback('onComplete', null)
			// Nothing is opening behind it: this fade is the end of the line until the plan is rebuilt.
			this._settleChild(child, -1, onSettle)
			onEnd?.()
		})
	}

	isActive() {
		// A finished timeline is not an active one. `timeline` is only nulled by kill(), so without the progress check this stays true forever after any completed transition (playbackSync would then call joinTransition() against a dead timeline).
		return !!this.timeline && this.timeline.progress() < 1
	}

	// A fade is at the playhead and has not been settled — TRUE at progress 1, unlike isActive(). ⚠️ Never `!!this._parent`, which is true across holds: the transport buttons would go live with no fade to drive.
	isLive() {
		return this._child()?.kind === 'fade'
	}

	// The fade's position, straight off the timeline — the only copy; nothing stores it. Unlike a morph's `t`, a pinned param cannot freeze it.
	getProgress() {
		return this.timeline ? this.timeline.progress() : 0
	}

	// Whether the clock is frozen. The mirror of `transition_paused`, read straight from GSAP for callers that also ask `isReversed` — the two must describe the same instant, and a store round-trip can lag a seek by a dispatch.
	isPaused() {
		return !!this._clock()?.paused()
	}

	// Which way the timeline is currently playing. GSAP direction is STICKY — a `reverse()` survives any later pause — so a caller with no cell to name an end (the playing pie) must ask the timeline rather than read `transition_reversed`, which records the GESTURE that opened the fade and not where it is headed now.
	isReversed() {
		return !!this.timeline?.reversed()
	}

	// The state this fade started from, the only honest origin for a DETACHED one. `terminal` drops objects that exist only because this fade owns them — correct when nothing will animate them next.
	fromState({ terminal = false } = {}) {
		if (!terminal || !this._fromState?.objects) return this._fromState
		const child = this._child()
		if (!child?._owned?.size) return this._fromState
		const live = store.getState().live?.objects ?? {}
		const objects = {}
		for (const [id, obj] of Object.entries(this._fromState.objects)) {
			// Owned and declared by no live preset: it exists only for the fade being abandoned.
			if (id !== 'root' && child._owned.has(id) && !(id in live)) continue
			objects[id] = obj
		}
		// Parentage is stored twice; `children` must not name an id that just left.
		for (const id in objects) {
			const kids = objects[id].children
			if (kids?.some(k => !(k in objects)))
				objects[id] = { ...objects[id], children: kids.filter(k => k in objects) }
		}
		return { ...this._fromState, objects }
	}

	// ── Scrubbing ─────────────────────────────────────────────────────────────────────────────
	//
	// Direct manipulation of the running fade. `pause` and `resume` are GSAP's own (its ticker drives transitions; only PixiJS's is stopped), and freezing the timeline freezes the morph proxies with it, so the server holds the blend it is showing rather than sliding on to `b` under a still image.
	pause() {
		// ⚠️ Freezing a fade inside a run is the user taking it over, so the run is parked and the child detached HERE rather than at each gesture that follows. Once handed off, `_clock()` is the fade itself and `resume`/`seek`/`complete` drive it without ever re-entering the plan.
		this._handOffChild()
		const clock = this._clock()
		if (!clock || clock.paused()) return
		clock.pause()
		store.dispatch(setTransitionPaused(true))
	}

	resume() {
		const clock = this._clock()
		if (!clock || !clock.paused()) return
		// Handing the clock back: the fade runs to its own end again, so its settle must be armed. A scrub that closed the gate never resumes — it finishes through `complete` or is abandoned.
		this._settling = true
		clock.resume()
		store.dispatch(setTransitionPaused(false))
	}

	/**
	 * Move the fade to `t`, leaving it paused there.
	 *
	 * Events are NOT suppressed: a seek runs the same `onUpdate` chain a playing frame does, so a derived value lands however the playhead moved. `_settling` holds teardown off instead.
	 */
	seek(t) {
		if (!this.timeline) return
		// ⚠️ A scrub under a LIVE parent would leave children `_at+1..N` with open settle gates while the playhead marched past them. The gesture takes the fade off the plan first — the plan itself is parked, not dropped.
		this._handOffChild()
		// The user owns the playhead now; crossing the end is a drag, not a finish.
		this._settling = false
		this.pause()
		this.timeline.progress(Math.min(Math.max(t, 0), 1))
	}

	/**
	 * Let a scrubbed fade finish: run it out from where it sits, or settle a fade already at the end.
	 *
	 * ⚠️ At `progress === 1` there is no crossing left to make, so the gate opens and the end is re-crossed by hand.
	 */
	complete() {
		if (!this.timeline) return
		// Direction is STICKY: a fade `reverse()` turned around stays reversed through any later pause, so a plain resume runs it back to the origin and `onComplete` never fires.
		this.timeline.eventCallback('onReverseComplete', null)
		this._settling = true
		if (this.timeline.progress() >= 1) {
			this.timeline.progress(0.999)
			this.timeline.progress(1)
		}
		this.timeline.play()
		// The gesture that froze this fade already handed it off the plan, so the clock here IS the fade — `play()` above and this agree on one timeline.
		this._clock()?.resume()
		store.dispatch(setTransitionPaused(false))
	}

	/**
	 * Play the fade BACKWARDS to its origin, at the rate it would have run forwards.
	 *
	 * ⚠️ At `progress === 0` there is no time left to play backwards, so it is nudged off zero to have a crossing to make. The forward settle must not fire for a destination being abandoned, so the caller settles the origin.
	 */
	reverse(onDone) {
		if (!this.timeline) return
		// ⚠️ The PARENT is never reversed: GSAP re-fires child callbacks on a backwards crossing, so every builder down the chain would re-run and rebuild the scenes it already built. Park the run and turn the fade around on its own.
		//
		// ⚠️ Pausing the parent is not enough — a child of a paused parent cannot advance in EITHER direction, so a reverse would silently freeze at the progress it was called at. `_handOffChild` takes it off the parent and onto the global timeline, which is what gives it a clock of its own.
		this._handOffChild()
		if (this.timeline.progress() <= 0) this.timeline.progress(0.001)
		this.timeline.eventCallback('onReverseComplete', () => {
			this.timeline?.eventCallback('onReverseComplete', null)
			onDone?.()
		})
		this.timeline.reverse()
		store.dispatch(setTransitionPaused(false))
	}

	// ── Settling ───────────────────────────────────────────────────────────────────────────
	//
	// Everything a fade builds on the stage is registered here WITH ITS UNDO, at the moment it is built; both exits then run one loop.
	//
	// ⚠️ ORDER IS REGISTRATION ORDER. Ownership resolves before the visibility settle, and that lands before `pixi_transition_active` drops — so the next updatePixiObject re-syncs from Redux on top of it.
	//
	// ⚠️ TWO DIFFERENT "next"s reach a settler, and conflating them strands objects. `owns` is what owned objects are handed to; `successor` means "another fade is still animating this, leave its flags alone". They coincide on an interrupt and differ on a natural completion, where the destination owns the objects but nothing is animating them. Each settler names which it takes, so neither is guessed from its identity.
	_registerSettle(child, apply) {
		;(child._settlers ??= []).push(apply)
	}

	/**
	 * Register a settler that runs ONCE however many times its registry is appended to. Returns the list, created on first call.
	 *
	 * The registries are built lazily inside per-object loops, so the alternative is a `if (!list) { list = []; register() }` preamble at every push site — four of them, two identical.
	 */
	_settleList(child, key, apply) {
		if (!child[key]) {
			child[key] = []
			this._registerSettle(child, apply)
		}
		return child[key]
	}

	/**
	 * Undo everything this fade put on the stage, in registration order.
	 *
	 * ⚠️ `getActiveState()` reads the crossing registries, so it must run BEFORE this clears them.
	 *
	 * @param owns       objects map owned objects resolve against; falls back to Redux `live`.
	 * @param successor  objects a takeover fade will keep animating, or null when none is.
	 * @param carry      the successor is being built and will keep animating — see `_releaseOwnership`.
	 */
	_settleScene(child, { owns = null, successor = null, carry = false, handingOver = false } = {}) {
		if (!child?._settlers) return
		// ⚠️ Taken BEFORE the loop and each settler isolated: a throw must neither skip the rest (the last lowers `pixi_transition_active`) nor leave the list for a later kill to re-run.
		const settlers = child._settlers
		child._settlers = null
		getEngine()?.endFade()
		for (const apply of settlers) {
			try {
				apply({ owns, successor, carry, handingOver })
			} catch (e) {
				logger.error('settle failed', e)
			}
		}
	}

	// ── Object ownership ───────────────────────────────────────────────────────────────────
	//
	// Objects the transition creates (absent from fromState) must not be in pixi_objects_map:
	// Redux does not know them yet, and React's cleanup destroys any mapped id outside the active tree — mid-fade, while tweens still hold the reference. So the transition owns them until it can hand them somewhere safe.
	//
	// ONE registry, resolved against whatever state comes next. No "the caller promises to call createTransition" flag: a promise that instant loads and unmounts silently break is what stranded objects on the stage.
	_own(child, id, pixiObj, type, origin) {
		if (!child._owned) child._owned = new Map()
		const prev = child._owned.get(id)
		child._owned.set(id, {
			id,
			pixiObj,
			type,
			origin,
			fill: prev?.fill ?? null,
			fillBacking: prev?.fillBacking ?? null,
			realId: prev?.realId,
		})
	}

	/**
	 * Resolve every owned object against the state that is about to be rendered, then let go. Called on EVERY exit: completion, kill(), and the start of the next transition.
	 *
	 * @param nextObjects  objects of the state taking over, or null when nothing is queued.
	 * @param carry        a new transition is being built and will keep animating. Nothing is
	 *                     destroyed and nothing is handed to React: an object still on screen must be FADED out by the incoming timeline, not deleted under the user's eyes, and React must not see it until Redux does (its cleanup would destroy it mid-fade and take the timeline's onComplete with it).
	 *
	 * ⚠️ Destroying on `carry` is a vanish, not a cleanup. An object created for the outgoing fade sits at whatever alpha it reached (0.4, say); if the new destination drops it, that is a fade-OUT to run, identical to how an adopted object the destination drops is handled. Only a terminal release (carry=false — completion, standalone kill) may destroy, because then nothing is left to animate it.
	 */
	_releaseOwnership(child, nextObjects = null, carry = false) {
		if (!child._owned) return
		const next = nextObjects ?? store.getState().live?.objects ?? {}
		const kept = new Map()
		// A retyped object's old pixiObj goes before its replacement takes the id: a Sprite cannot draw a path.
		const wrongType = e => !carry && (e.realId ?? e.id) in next && next[e.realId ?? e.id]?.type !== e.type
		const entries = [...child._owned.values()].sort((a, b) => wrongType(b) - wrongType(a))
		for (const entry of entries) {
			const { pixiObj, type, origin } = entry
			const id = entry.realId ?? entry.id
			if (pixiObj.destroyed) continue
			if (wrongType(entry)) {
				const mapped = window.pixi_objects_map?.get(id)
				window.pixi_destroy_fill?.(
					mapped?.pixiObj === pixiObj ? id : entry.id,
					mapped?.pixiObj === pixiObj ? mapped : entry
				)
				pixiObj.parent?.removeChild(pixiObj)
				pixiObj.destroy()
				if (mapped?.pixiObj === pixiObj) window.pixi_objects_map.delete(id)
			} else if (carry) {
				// Keep owning it either way. In `next` → the incoming timeline fades it in/along;
				// absent from `next` → the incoming timeline gives it a fade-OUT (createTransition sees it via _ownedPixiObj and takes the !toObj branch).
				kept.set(entry.id, entry)
			} else if (id in next) {
				// Redux has it now; an adopted one is still mapped, with its own fill.
				if (window.pixi_objects_map?.get(id)?.pixiObj !== pixiObj)
					window.pixi_objects_map?.set(id, {
						pixiObj,
						type,
						fill: entry.fill,
						fillBacking: entry.fillBacking,
					})
			} else if (origin === 'created') {
				// Terminal: existed only for this transition and nothing is left to animate it.
				window.pixi_destroy_fill?.(entry.id, entry)
				pixiObj.parent?.removeChild(pixiObj)
				pixiObj.destroy()
				if (window.pixi_objects_map?.get(id)?.pixiObj === pixiObj) window.pixi_objects_map.delete(id)
			}
			// origin 'adopted' + absent from next: it belongs to some preset, just not this one. React owns its lifetime; the visibility settle below restores its flags.
		}
		child._owned = kept.size ? kept : null
	}

	// Settle objects whose `visible` flag was forced true for the duration of a fade. Both endpoints must run this: the timeline `.call()` at `duration` for the normal path, and kill() for an interrupt — on interrupt updatePixiObject cannot repair it, because Redux is still at the FROM preset where a fading-out object was shown, so it would re-assert visible=true and strand the object on stage at partial opacity.
	//
	// `nextObjects`: when a new transition is taking over and it also crosses this object's visibility, leave the object shown at its current alpha so the incoming fade continues from there. Otherwise settle — a half-faded object left `visible=true` with nothing animating it is a stranded object on stage.
	_settleVisibilityCrossings(child, nextObjects = null) {
		if (!child._visibilityCrossings) return
		for (const { id, pixiObj, visible, opacity } of child._visibilityCrossings) {
			if (pixiObj.destroyed) continue
			// Hand off only if the successor still has this object to animate.
			if (nextObjects && id in nextObjects) continue
			pixiObj.visible = visible
			setOpacity(pixiObj, opacity)
		}
		child._visibilityCrossings = null
	}

	// ── Mask crossings ────────────────────────────────────────────────────────────────────────
	//
	// `mask` is a structural prop (an object id, not a number) so the generic tween never sees it, and React applies masks from Redux — which stays at the FROM preset for the whole morph. The link therefore flipped in a single frame at completion, which is the whole bug: a path arriving as a mask first faded in as a white DRAWING and only became a stencil at the end, and a path leaving faded the *stencil* to alpha 0 — an alpha mask multiplies, so the masked video dissolved to black and then snapped back unmasked.
	//
	// The fix keys on the LINK delta, never on the path's presence: a path can exist on both sides and merely be linked on one, which has no appear/disappear for the engine to notice.
	//
	// Coverage is animated on a dedicated full-stage rect inside a TEMPORARY mask container, because fading an alpha mask toward 0 means "erase everything", not "stop masking". Rect at alpha 1 = uniformly opaque mask = provably a no-op; rect at 0 = the path alone. So masking tweens the rect 1→0 and unmasking 0→1, and the settle drops the mask entirely rather than leaving a degenerate one installed.
	//
	// The container is FRESH and identity-transform, never the preset's own mask group: a mask group may be scaled/rotated/positioned (SVG imports always are) and the transition tweens that group's scale via _scaleTransitionProxy, so a rect parented inside it would inherit the transform and visibly creep off the stage mid-fade. Identity container + clones carrying resolved WORLD pose keeps the rect stage-correct by construction.
	_buildMaskContainer(child, maskId, objectsMap, pinnedIds = null) {
		const app = window.pixi_app
		if (!app || !window.pixi_create_object || !window.pixi_update_object) return null
		if (!window.pixi_create_container || !window.pixi_create_cover_rect) return null
		const maskObj = objectsMap.get(maskId)
		if (!maskObj) return null

		// Constructed through the same window seam as every other display object the engine makes (pixi_create_object), rather than importing the pixi classes here: the manager stays decoupled from the renderer, which is what lets the unit suite drive it over fakes.
		const container = window.pixi_create_container()
		// Flagged so the monkey-patched AlphaMask.test accepts a Container (usePixiObjects.js) — coverage must multiply, which a stencil mask cannot express.
		container._useAlphaMask = true

		// Full-stage coverage rect at identity. White + opaque: the red channel is what an alpha mask samples.
		const rect = window.pixi_create_cover_rect(app.screen.width, app.screen.height)
		container.addChild(rect)

		// Clone every leaf of the mask (a mask may be a single path or a group of them) at its resolved WORLD pose. Cloning — rather than moving the real object in — is what lets the original keep rendering as an ordinary drawing while the clone does stencil duty: PixiJS does not render a container assigned as a mask, so putting the real path in would make it vanish instantly with nothing left to fade. It also keeps the white recolor off the user's object.
		const clones = []
		const addClone = (obj, worldT) => {
			const src = {
				...obj,
				x: worldT.x,
				y: worldT.y,
				rotation: worldT.rotation,
				scaleX: worldT.scaleX,
				scaleY: worldT.scaleY,
				parentId: 'root',
			}
			const clone = window.pixi_create_object(src)
			if (!clone) return
			ungated(() => window.pixi_update_object(clone, src))
			// An unfilled path contributes nothing to an alpha mask — force white, exactly as React's mask pass does, but on the clone so the real object's style is never destroyed.
			if (obj.type !== 'group' && !obj.fill && obj.blendMode !== 'destination-out' && clone.updateStyle) {
				clone.updateStyle({ color: 0xffffff, alpha: 1 }, obj.stroke, obj.strokeWidth)
				clone._draw?.()
			}
			clone.alpha = 1
			clone.visible = true
			container.addChild(clone)
			// Following the live object is right only while it animates toward this stencil pose; one pinned to the other side by a crossing would drag the stencil onto the wrong geometry.
			clones.push({ id: obj.id, clone, pinned: pinnedIds?.has(obj.id) === true })
		}

		// Every drawable LEAF under the mask, at any depth. An SVG mask is routinely a group of groups, and a group draws nothing itself — collecting only direct children finds no leaves and builds an empty stencil.
		if (maskObj.type === 'group' && maskObj.children?.length) {
			const seen = new Set([maskObj.id])
			const collect = (groupObj, depth) => {
				if (depth > 16) return
				for (const childId of groupObj.children ?? []) {
					if (seen.has(childId)) continue
					seen.add(childId)
					const childObj = objectsMap.get(childId)
					if (!childObj) continue
					if (childObj.type === 'group') collect(childObj, depth + 1)
					else addClone(childObj, resolveWorldTransform(childObj, objectsMap))
				}
			}
			collect(maskObj, 0)
		} else {
			addClone(maskObj, resolveWorldTransform(maskObj, objectsMap))
		}

		if (!clones.length) {
			container.destroy({ children: true })
			return null
		}

		app.stage.addChild(container)
		return { container, rect, clones }
	}

	// RELINK: masked by one path in FROM and a DIFFERENT one in TO. Both endpoints are masked, so there is no "unmasked" end for the coverage rect to travel toward — the rect axis spans path⇄everything and cannot express path⇄other-path.
	//
	// Two strategies, chosen on shape:
	//
	// • MORPH (both sides a single non-group path). One clone whose geometry is tweened from the FROM path to the TO path via MorphPlugin — the same mechanism the generic `data` tween uses — with its transform tweened alongside, since the two paths generally sit at different world poses. Coverage stays BINARY the whole way: every pixel is either masked or not, and only the boundary moves. This is what makes it better than a crossfade rather than merely different.
	//
	// • CROSSFADE (anything else — either side a group, e.g. an SVG import). Clone both sides and dissolve one into the other. Accepted cost: mid-fade both clone sets are semi-transparent, and because an alpha mask multiplies, the video shows partial coverage in BOTH regions at once — a brief double-exposure where they do not overlap. A group has no single path to morph, so there is nothing better available without decomposing it.
	_buildMaskRelink(child, fMaskId, tMaskId, fromMap, toMap, vars) {
		const fromMaskObj = fromMap.get(fMaskId)
		const toMaskObj = toMap.get(tMaskId)
		if (!fromMaskObj || !toMaskObj) return null

		const morphable = o => o && o.type !== 'group' && typeof o.data === 'string' && o.data.length
		if (morphable(fromMaskObj) && morphable(toMaskObj)) {
			// Build the container around the FROM path, then tween its single clone to the TO path.
			const built = this._buildMaskContainer(child, fMaskId, fromMap)
			if (!built) return null
			const { container, rect, clones } = built
			// No coverage animation on a relink — the mask is fully "on" at both ends.
			rect.alpha = 0
			const clone = clones[0]?.clone
			if (!clone) return null

			// FROM pose is already on the clone (_buildMaskContainer resolved it); only the destination is needed.
			const toW = resolveWorldTransform(toMaskObj, toMap)
			clone._rawPath = stringToRawPath(fromMaskObj.data)
			child.tl.to(
				clone,
				{
					...vars,
					morph: stringToRawPath(toMaskObj.data),
					x: toW.x,
					y: toW.y,
					rotation: (toW.rotation * Math.PI) / 180,
				},
				0
			)
			// scale lives on a nested object, so it needs its own tween target.
			child.tl.to(clone.scale, { ...vars, x: toW.scaleX, y: toW.scaleY }, 0)
			// Pose is being tweened here — the per-frame clone sync must not fight it.
			return { container, rect, clones: [], masking: false, relink: 'morph' }
		}

		// Crossfade: FROM set fades out, TO set fades in, inside one container.
		const built = this._buildMaskContainer(child, fMaskId, fromMap)
		if (!built) return null
		const { container, rect, clones } = built
		rect.alpha = 0
		const incoming = this._buildMaskContainer(child, tMaskId, toMap)
		if (!incoming) return null
		// Move the incoming clones into the FROM container and drop the spare shell + its rect.
		for (const entry of incoming.clones) {
			entry.clone.alpha = 0
			container.addChild(entry.clone) // reparents: addChild removes from the previous parent first
			child.tl.to(entry.clone, { ...vars, alpha: 1 }, 0)
		}
		// Only the spare coverage rect is left inside the shell by now — the clones were reparented above, so `children: true` cannot reach them.
		incoming.container.parent?.removeChild(incoming.container)
		incoming.container.destroy({ children: true })
		for (const { clone } of clones) child.tl.to(clone, { ...vars, alpha: 0 }, 0)
		// Both sets are pose-tweened by nothing, so keep syncing the FROM set only.
		return { container, rect, clones, masking: false, relink: 'crossfade' }
	}

	// Re-resolve each clone's world pose from the live object. The clone is decoupled from the real mask path, so when the path is ALSO animating during the transition (position, scale, a `data` morph) the stencil would drift out of alignment with the drawing. One transform copy per frame beats duplicating every tween, and it stays correct across an interrupt.
	_syncMaskClones(child) {
		if (!child._maskCrossings) return
		for (const { clones } of child._maskCrossings) {
			for (const entry of clones) {
				const { id, clone, pinned } = entry
				if (clone.destroyed || pinned) continue
				const live = window.pixi_objects_map?.get(id)?.pixiObj ?? this._ownedPixiObj(id, child)
				if (!live) continue
				const wt = live.parent && live.parent !== window.pixi_app?.stage ? live.worldTransform : null
				if (wt) {
					// `worldTransform.tx/ty` is the transformed local origin. For vectors,
					// Pixi's local origin is offset by pivot; clones sit on the identity
					// stage, so give them the transformed pivot position as their x/y.
					const px = live.pivot?.x ?? 0
					const py = live.pivot?.y ?? 0
					clone.x = wt.tx + wt.a * px + wt.c * py
					clone.y = wt.ty + wt.b * px + wt.d * py
					clone.rotation = Math.atan2(wt.b, wt.a)
					clone.scale.x = Math.sign(wt.a || 1) * Math.hypot(wt.a, wt.b)
					clone.scale.y = Math.sign(wt.d || 1) * Math.hypot(wt.c, wt.d)
				} else {
					clone.x = live.x
					clone.y = live.y
					clone.rotation = live.rotation
					clone.scale.x = live.scale?.x ?? 1
					clone.scale.y = live.scale?.y ?? 1
				}
				// The world transform translation above is the live object's pivot position. Keep
				// the clone's local origin in sync too: when a pivot/anchor tweens, leaving the
				// clone's old origin makes the stencil drift toward the object's top-left.
				if (live.pivot && clone.pivot) {
					clone.pivot.x = live.pivot.x
					clone.pivot.y = live.pivot.y
				}
				if (live.anchor && clone.anchor) {
					clone.anchor.x = live.anchor.x
					clone.anchor.y = live.anchor.y
				}
				// ⚠️ The morph mutates `_rawPath` in place, so a reference check never fires and the copy must be deep; its `rawPath` setter bumps `_rawPathVersion`, which says when.
				if (live._rawPath && entry.version !== live._rawPathVersion) {
					entry.version = live._rawPathVersion
					clone._rawPath = live._rawPath.map(seg => (Array.isArray(seg) ? seg.slice() : seg))
					clone._draw?.()
				}
			}
		}
	}

	/**
	 * Morph the coverage shape between the full stage and the stencil's own geometry.
	 *
	 * ⚠️ Never fade the rect's ALPHA: an alpha mask multiplies, so a stage-wide rect at 0.5 passes the whole object through at half strength instead of cutting it. Coverage must stay binary, and only a moving boundary expresses that.
	 */
	_buildCoverageMorph(child, rect, clones, masking, vars) {
		const live = clones.map(c => c.clone).filter(c => c && !c.destroyed && c._rawPath?.length)
		if (!live.length || !rect._rawPath?.length || typeof rect._draw !== 'function') {
			rect.alpha = 1
			return false
		}
		const full = rect._rawPath.map(s => s.slice())
		// ⚠️ Compose from the clone's own transform, never `worldTransform`: the container has not rendered at build time, so that matrix is still identity and the geometry bakes at the origin.
		const worldPath = c => {
			const cos = Math.cos(c.rotation || 0)
			const sin = Math.sin(c.rotation || 0)
			const sx = c.scale?.x ?? 1
			const sy = c.scale?.y ?? 1
			const tx = c.x || 0
			const ty = c.y || 0
			const px = c.pivot?.x ?? 0
			const py = c.pivot?.y ?? 0
			const out = []
			for (const seg of c._rawPath) {
				if (!Array.isArray(seg)) continue
				const copy = seg.slice()
				for (let i = 0; i < copy.length; i += 2) {
					// `x/y` names the pivot position. Baking the shape into stage space
					// must apply Pixi's (point - pivot) transform before resetting it.
					const x = (copy[i] - px) * sx
					const y = (copy[i + 1] - py) * sy
					copy[i] = x * cos - y * sin + tx
					copy[i + 1] = x * sin + y * cos + ty
				}
				out.push(copy)
			}
			return out
		}
		// Collapse a shape onto its own centre: the state a leaf grows out of, and shrinks back into.
		const seed = path => {
			let n = 0
			let cx = 0
			let cy = 0
			for (const s of path)
				for (let i = 0; i < s.length; i += 2) {
					cx += s[i]
					cy += s[i + 1]
					n++
				}
			if (!n) return path.map(s => s.slice())
			cx /= n
			cy /= n
			return path.map(s => s.map((_, i) => (i % 2 ? cy : cx)))
		}
		// ⚠️ Each leaf keeps its OWN fill and morphs alone. Unioned into one shape they take one fill, and a black leaf — a HOLE, since an alpha mask reads the red channel — turns white and fills in.
		let built = false
		for (const clone of live) {
			const target = worldPath(clone)
			if (!target.length) continue
			// Only a white leaf may sweep from the full stage; starting a hole stage-wide would blank everything it covers.
			const isHole = (clone._fill?.color ?? 0xffffff) === 0
			const open = isHole ? seed(target) : full
			clone.visible = true
			clone.x = 0
			clone.y = 0
			clone.rotation = 0
			clone.scale?.set?.(1, 1)
			if (clone.pivot?.set) clone.pivot.set(0, 0)
			else if (clone.pivot) {
				clone.pivot.x = 0
				clone.pivot.y = 0
			}
			clone.setRawPathSilent?.(masking ? open.map(s => s.slice()) : target.map(s => s.slice()))
			clone._draw()
			child.tl.to(clone, { ...vars, morph: masking ? target : open }, 0)
			// The per-frame clone sync re-resolves pose and geometry off the live object; here the tween owns both.
			const entry = clones.find(c => c.clone === clone)
			if (entry) entry.pinned = true
			built = true
		}
		if (!built) {
			rect.alpha = 1
			return false
		}
		// The clones carry every contour now; a second full-stage cover on top would fill the holes back in.
		rect.alpha = 0
		return true
	}

	// The stencil is invisible as a mask, but its alpha is the object's own and outlives the fade — a later preset that unmasks it would show nothing.
	_settleMaskStencilFades(child) {
		if (!child._maskStencilFades) return
		for (const { stencil, opacity } of child._maskStencilFades) {
			if (!stencil.destroyed) setOpacity(stencil, opacity)
		}
		child._maskStencilFades = null
	}

	// Runs at BOTH exits, like the visibility settle. A natural completion hands an existing destination stencil over before dropping coverage; if React has not created a destination-only stencil yet, keep the completed coverage container installed until its reconcile replaces it. An interrupt still drops the outgoing coverage so the successor can build from its active state.
	//
	// `nextObjects`: when the successor transition crosses this same object's mask it rebuilds its own container from the current state, so tear this one down regardless — but never touch `pixiObj.mask` when an eraser mask owns it (usePixiObjects keeps `_eraserMaskGfx` on the same property).
	_settleMaskCrossings(child, { owns = null, carry = false } = {}) {
		if (!child._maskCrossings) return
		for (const { id, pixiObj, container, toMaskId, maskInverse = false } of child._maskCrossings) {
			let handoffContainer = false
			const targetMaskId = !carry && owns?.[id]?.mask === toMaskId ? toMaskId : null
			if (targetMaskId && !pixiObj.destroyed && !pixiObj._eraserMaskGfx) {
				// Swap to the destination stencil before removing coverage when its real
				// Pixi object already exists; this makes the last rendered frame atomic.
				const stencil = window.pixi_objects_map?.get(targetMaskId)?.pixiObj
				if (stencil && !stencil.destroyed) {
					stencil._useAlphaMask = true
					pixiObj.setMask({ mask: stencil, inverse: maskInverse })
					pixiObj._maskInverse = maskInverse
				} else {
					// A stencil that exists only in the destination has no real Pixi
					// object until React reconciles the new Redux tree. Keep the fully
					// morphed coverage mask installed until that reconcile replaces it.
					pixiObj._transitionMaskContainer = container
					handoffContainer = true
				}
			} else if (!pixiObj.destroyed && !pixiObj._eraserMaskGfx) {
				clearMask(pixiObj)
				pixiObj._maskInverse = undefined
			}
			if (!handoffContainer && !container.destroyed) {
				container.parent?.removeChild(container)
				container.destroy({ children: true })
			}
		}
		child._maskCrossings = null
	}

	// ── Ghost crossfades ──────────────────────────────────────────────────────────────────────
	//
	// Changes no tween crosses: a mirror sign lerps through 0 or pops by its own width, a depth swap is one frame either way.

	// ⚠️ The copy is a STILL. Two live copies of one video show identical motion in mirrored places — ghosting, not a cut. ⚠️ Freeze only; `_placeGhosts` sets the depth after `_applyStageOrder`.
	// ⚠️ `dissolve` only where the picture differs: an identical copy dips coverage to 0.75 midway.
	_crossfadeFrom(child, id, pixiObj, vars, toOpacity = 100, dissolve = true, fadeGhost = true) {
		if (!this._freezeGhost(child, id, pixiObj, vars, fadeGhost, true)) return false
		const fade = child._fillCrossfades?.find(f => f.id === id)
		const entry = window.pixi_objects_map?.get(id) ?? child._owned?.get(id)
		for (const node of fade ? fade.olds.map(o => o.node) : [entry?.fill])
			if (node && !node.destroyed && node.visible && node.alpha > 0)
				this._freezeGhost(child, id, node, vars, fadeGhost, false, pixiObj)

		if (dissolve) {
			setOpacity(pixiObj, 0)
			child.tl.to(pixiObj, { ...vars, ...setOpacityVars({}, pixiObj, toOpacity) }, 0)
		}
		return true
	}

	/** A still of `pixiObj` at its FROM box, on the stage, registered for `_syncGhosts`/`_placeGhosts`. `reuse`: a sprite's own texture stands in for a render; a fill node's is its live source, never a still. */
	_freezeGhost(child, id, pixiObj, vars, fadeGhost, reuse, anchor = null) {
		const app = window.pixi_app
		if (!app || !pixiObj || pixiObj.destroyed) return false

		let ghost = null
		let generated = null
		try {
			// ⚠️ `instanceof`, never truthiness: `PixiShape` inherits Graphics' `texture` METHOD, so a bare check freezes a 1×1 nothing and a depth swap pops under an invisible ghost. Reuse a real source texture; `generateTexture` promotes its subject to its own render group.
			const own = pixiObj.texture
			const wasRenderGroup = pixiObj.isRenderGroup
			const texture =
				reuse && own instanceof Texture && own !== Texture.EMPTY
					? own
					: (generated = app.renderer.generateTexture(pixiObj))
			// ⚠️ The promotion OUTLIVES the fade, and an alpha mask cannot sample across a render-group boundary — a masked subject stays promoted and renders as fully cut for every later preset. Hand it back the grouping it had.
			if (generated && !wasRenderGroup && pixiObj.isRenderGroup) pixiObj.disableRenderGroup?.()
			ghost = window.pixi_create_sprite?.(texture) ?? null
			if (!ghost) {
				generated?.destroy?.(true)
				return false
			}
		} catch (e) {
			logger.warn('mirror crossfade: could not freeze', id, e)
			return false
		}

		// ⚠️ Measure UNMASKED, matching `_syncGhosts`: the still holds the subject's whole picture, so sizing it to a mask-clipped box shrinks it away from what it captured.
		const box = unmaskedBounds(pixiObj)
		const bw = box.width ?? box.maxX - box.minX
		const bh = box.height ?? box.maxY - box.minY
		// The still holds the subject without its transform, so it takes every mirror above it too: a group's is the subject's.
		let sgx = 1
		let sgy = 1
		for (let n = pixiObj; n && n !== app.stage; n = n.parent) {
			sgx *= signOf(n.scale.x)
			sgy *= signOf(n.scale.y)
		}
		ghost.scale.x = (bw / (ghost.texture?.width || 1)) * sgx
		ghost.scale.y = (bh / (ghost.texture?.height || 1)) * sgy
		ghost.x = (box.x ?? box.minX) + (sgx < 0 ? bw : 0)
		ghost.y = (box.y ?? box.minY) + (sgy < 0 ? bh : 0)
		ghost.alpha = pixiObj.alpha ?? 1
		app.stage.addChild(ghost)

		if (fadeGhost) child.tl.to(ghost, { ...vars, alpha: 0 }, 0)

		// One id's ghosts are placed in push order, each landing below the last: the fill's under the shape's.
		const list = this._settleList(child, '_ghostCrossfades', () => this._settleGhostCrossfades(child))
		const ratio = anchor && !anchor.destroyed && followRatio(box, unmaskedBounds(anchor))
		list.push({ id, ghost, pixiObj, ownsTexture: !!generated, follow: ratio ? { anchor, ...ratio } : null })
		return true
	}

	// ⚠️ A ghost pinned to the FROM box is a still lingering in the old place once the fade also moves or resizes. Runs in the timeline's onUpdate, after every tween has written this frame.
	/** Textures a fade will need, resolved BEFORE it opens so a swapped image can fade in rather than pop when the load lands. */
	// ⚠️ `true` — NOT a resolved promise — when everything is resident: a promise costs a microtask and splits the caller's turn.
	preloadTextures(toState) {
		const sources = [
			...new Set(
				Object.values(toState?.objects ?? {})
					.filter(o => o?.type === 'image' && o.source)
					.map(o => o.source)
			),
		]
		// This fade's own map: a load resolving after a later preload must not land in that one's.
		const pending = new Map()
		this._pendingTextures = pending
		if (!sources.length) return true

		const missing = []
		for (const src of sources) {
			const tex = getCachedTexture(src)
			if (tex) pending.set(src, tex)
			else missing.push(src)
		}
		if (!missing.length) return true

		return Promise.all(
			missing.map(src =>
				(loadImageTextureFor(src) ?? Promise.resolve(null))
					.then(tex => tex && pending.set(src, tex))
					.catch(e => logger.warn('texture preload failed', src, e))
			)
		)
	}

	/** A fill source change: the TO node dissolves in over the held outgoing node and any an interrupted dissolve handed over. */
	_crossfadeFill(child, id, toObj, toMap, toState, vars, mirrored = false) {
		const entry = window.pixi_objects_map?.get(id)
		if (!entry || !window.pixi_swap_fill) return
		const carried = this._takeFillCarry(child, id)
		const out = window.pixi_swap_fill(id, entry, toObj, { objects: toMap, bags: toState.source_settings })
		const still = mirrored && out?._sample && this._stillFill(child, out)
		const olds = out ? [{ node: out, release: out._sourceId && !still ? holdSource(out._sourceId) : null }] : []
		this._dissolveFill(child, id, entry, entry.fill, 0, [...olds, ...(carried?.olds ?? [])], vars)
	}

	/** Two subsets on one fill (or one subset whose bag changes its build): the node re-variants to their union, every path id tweens from A's vector to B's, and the node settles on the end it lands at. False when the pair has no path, so the fill crossfades. */
	_pathFill(child, id, fromObj, toObj, fromState, toState, liveProps, vars) {
		if (!isSourceFill(fromObj.fill) || !isSourceFill(toObj.fill)) return false
		const aSource = fromObj.fill.source
		const source = toObj.fill.source
		const a = getShader(aSource)
		const b = getShader(source)
		if (!a?.locks || !b?.locks) return false
		if (aSource !== source && !isPathed(a.name, b.name)) return false
		const node = window.pixi_objects_map?.get(id)?.fill
		if (!node || node.destroyed || node._sample || !node.shader) return false
		// An interrupted path hands on the vector it showed.
		const fromBag = { ...sourceValues(fromState, aSource), ...node._path?.values }
		const toBag = sourceValues(toState, source)
		// One subset holding the same ids, count and spread still: the values blend in place. Their change walks the path, which grows lines in evenly and moves each palette element once.
		if (
			aSource === source &&
			sameIds(heldIds(a, fromBag), heldIds(b, toBag)) &&
			!steppedMoves(a, fromBag, b, toBag)
		)
			return false
		const path = pathOf(a, b, fromBag, toBag, shaderFrame(node))
		const bound = boundParams(path.shader, path.variant)
		// The ids a mid-fade write retargets: those B's bag sets.
		const bFree = new Set(heldIds(b, toBag))
		const start = { ...toBag, ...path.from }
		const pathEntries = edits => {
			const entries = []
			for (const key of path.variant.free) {
				const def = bound[key]
				const from = path.from[key]
				const bridge = path.bridge[key]
				// A pin holds where B exposes the id, an edit lands where B's build binds it, each inside B's fader; elsewhere B's lock wins.
				const fader = () => pathRange(b, key, { bag: toBag, fader: true })
				const pinned = key in b.params && isPinned(liveProps, sourcePinKey(source, key))
				const to = pinned
					? clampToRange(from, fader())
					: key in edits && bFree.has(key)
						? clampToRange(edits[key], fader())
						: path.to[key]
				if (JSON.stringify(from) === JSON.stringify(to)) continue
				if (isColorParam(def))
					entries.push({
						key,
						color: true,
						from: uniformValue(from, def),
						bridge: uniformValue(bridge, def),
						to: uniformValue(to, def),
					})
				else if (Array.isArray(from) || Array.isArray(to)) {
					const v = x => uniformValue(x, def)
					entries.push({ key, vec: true, from: v(from), bridge: v(bridge), to: v(to) })
				} else entries.push({ key, from, bridge, to, step: classifyParam(def).discrete })
			}
			return pathTween(entries, { overlap: pathOverlap(a.name, b.name), still: start, fit: path.fit })
		}
		let tween = pathEntries({})
		if (!switchShaderVariant(node, path.shader, path.variant, start)) return false
		node._fillSource = source
		const proxy = { t: 0 }
		const record = { id, child, node, proxy, values: start, a, aSource, b, source, fromBag, toBag, edits: {} }
		node._path = record
		const apply = () => {
			if (node.destroyed) return
			record.values = { ...start, ...tween(proxy.t) }
			writeShaderValues(node, record.values)
		}
		// A mid-fade write to B's bag (gesture frame, automation sample, edit) moves the path's target; B settles with it.
		record.retarget = edits => {
			if (Object.entries(edits).every(([k, v]) => JSON.stringify(v) === JSON.stringify(record.edits[k]))) return
			Object.assign(record.edits, edits)
			tween = pathEntries(record.edits)
			apply()
		}
		this._settleList(child, '_pathFills', ({ carry }) => this._settlePathFills(child, carry)).push(record)
		child.tl.to(proxy, { ...vars, t: 1, onUpdate: apply }, 0)
		return true
	}

	/** Each path's node onto the end its fade landed at; a successor takes a carried one over, still on the union. */
	_settlePathFills(child, carry) {
		const atEnd = !child.tl || child.tl.progress() >= 0.5
		for (const r of child._pathFills ?? []) {
			if (r.node.destroyed || r.node._path !== r) continue
			if (carry) r.carried = true
			else {
				settlePath(r, atEnd)
				// ⚠️ Mid-fade Redux still holds A's fills, so `setPruned` dropped these writes to B's bag; whoever lands B's settings writes them after (`takeLandedEdits`).
				if (atEnd)
					for (const [k, v] of Object.entries(r.edits))
						(child._landedEdits ??= []).push([sourceParamPath(r.source, k), v])
			}
		}
		child._pathFills = null
	}

	/** The writes to B's bag a settled path showed, as `setLiveProperties` entries; cleared on read. */
	takeLandedEdits(child = this._child()) {
		const out = child?._landedEdits ?? []
		if (child) child._landedEdits = null
		return out
	}

	/** The outgoing side of a mirror flip: a still of the source's whole frame, so it shows no live motion mirrored beside the incoming side, through its own view (the FROM mirror at the blended zoom and pan), so nothing is cropped. */
	_stillFill(child, node) {
		const app = window.pixi_app
		const frame = node._sampleTexture && new Texture({ source: node._sampleTexture })
		const sprite = frame && window.pixi_create_sprite?.(frame)
		if (!app || !sprite) {
			frame?.destroy(false)
			return false
		}
		let still
		try {
			still = app.renderer.generateTexture(sprite)
		} catch (e) {
			logger.warn('mirror crossfade: could not freeze the source', node._fillSource, e)
			return false
		} finally {
			sprite.destroy()
			// The wrapper only: its source is the node's live sample.
			frame.destroy(false)
		}
		bindSampleTexture(node, still)
		const source = node._fillSource
		const mirror = !!sourceValues(child._fromState, source).mirror
		this._settleList(child, '_stillFills', () => {
			// A carried node still binds its still: the texture goes with the node.
			for (const s of child._stillFills) {
				if (s.node.destroyed) s.still.destroy(true)
				else s.node.once('destroyed', () => s.still.destroy(true))
			}
			child._stillFills = null
		}).push({ node, still, source, mirror })
		return true
	}

	/** `next` rises from `from` once it has a frame (cut in past `CUT_IN_MS`); each outgoing node falls from the level it shows. */
	_dissolveFill(child, id, entry, next, from, olds, vars) {
		const shape = entry.pixiObj
		const fade = { id, shape, next, olds: olds.map(o => ({ ...o, level: o.node._fade ?? 1 })) }
		// The shape's own colour, when a source comes in over it: the snapshot names only the source.
		if (next)
			fade.colour = () => {
				const p = shape._fillTransitionProxy
				return p?.pair ? p.pair.at(p.t) : undefined
			}
		this._settleList(child, '_fillCrossfades', a => this._settleFillCrossfades(child, a)).push(fade)
		const ready = () => from > 0 || !next?._sample || (!!next._sourceId && hasFrame(next._sourceId))
		const gate = { t: 0, t0: null, cut: false }
		fade.apply = () => {
			if (gate.t0 === null && !gate.cut) {
				if (ready()) gate.t0 = gate.t
				// On the fade's clock, so a pause does not count toward the cut.
				else if (gate.t * vars.duration * 1000 > CUT_IN_MS) gate.cut = true
			}
			const m =
				gate.cut || gate.t0 >= 1 ? 1 : gate.t0 === null ? 0 : Math.max(0, (gate.t - gate.t0) / (1 - gate.t0))
			const pixiMap = window.pixi_objects_map
			if (next) {
				next._fade = from + (1 - from) * m
				followFill(next, shape, pixiMap)
			}
			// Stacked under one another: two nodes each wanting the slot under `next` would trade it every frame.
			let above = next ?? shape
			for (const o of fade.olds) {
				o.node._fade = o.level * (1 - m)
				followFill(o.node, shape, pixiMap, above)
				above = o.node
			}
		}
		child.tl.to(gate, { ...vars, t: 1 }, 0)
		fade.apply()
	}

	// ⚠️ MorphPlugin's morph is linear in progress, so two live samples place the outline at every key.
	_morphFillField(child, id, fromObj, toObj, toMap, toState, vars) {
		const entry = window.pixi_objects_map?.get(id)
		const node = entry?.fill
		const shape = entry?.pixiObj
		if (!node || node.destroyed || !shape || !fillBacking(toObj)) return
		if (fromObj.data === toObj.data || toObj.variableWidth || !node._mapping?.env) return
		if (JSON.stringify(fromObj.fill?.fit) !== JSON.stringify(toObj.fill?.fit)) return
		const env = node._mapping.env
		const nodeObj = fillNodeObj(toObj, toState.source_settings)
		const span = Math.min(1, FIELD_KEY_MS / Math.max(1, vars.duration * 1000))
		const lastKey = Math.ceil(1 / span) - 1
		const proxy = { t: 0 }
		child.tl.to(proxy, { ...vars, t: 1 }, 0)
		const samples = []
		const warmed = new Set()
		let seg = null
		let dead = false

		const liveObjects = () => liveEnv(env, window.pixi_objects_map).objects
		const objectsAt = p => {
			if (p >= 1) return toMap
			const [a, b] = samples
			const f = (p - a.q) / (b.q - a.q)
			const lerp = (x, y) => x + (y - x) * f
			const raw = b.raw.map((s, j) =>
				Object.assign(
					s.map((v, i) => lerp(a.raw[j][i], v)),
					{ closed: s.closed }
				)
			)
			const obj = { ...b.item, data: rawPathToString(raw) }
			for (const k of MORPH_POSE)
				if (Number.isFinite(a.item[k]) && Number.isFinite(b.item[k])) obj[k] = lerp(a.item[k], b.item[k])
			return new WithObject(liveObjects(), id, obj)
		}
		const endOf = k => Math.min(1, (k + 1) * span)
		const warm = k => {
			if (k > lastKey || warmed.has(k)) return
			warmed.add(k)
			const run = () => !dead && !node.destroyed && warmShaderFieldMorph(node, nodeObj, objectsAt(endOf(k)))
			if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: FIELD_KEY_MS })
			else setTimeout(run, 0)
		}
		const key = k => {
			const s0 = k * span
			const s1 = endOf(k)
			try {
				if (keyShaderFieldMorph(node, nodeObj, objectsAt(s0), nodeObj, objectsAt(s1))) return { k, s0, s1 }
			} catch (e) {
				logger.warn('fill field morph unavailable; following the outline', id, e)
			}
			return null
		}

		const drive = () => {
			if (dead || node.destroyed) return
			const q = proxy.t
			if (samples.length < 2) {
				const raw = shape._rawPath
				const item = liveObjects().get(id)
				if (!raw?.length || !item) return
				// Until the plugin has equalized the path, a sample has nothing to pair with.
				if (samples.length && !samePathShape(samples[0].raw, raw)) samples.length = 0
				else if (samples.length && q - samples[0].q < 1e-4) return
				samples.push({ q, raw: copyPath(raw), item: { ...item } })
				if (samples.length < 2) return
			}
			const k = Math.min(lastKey, Math.max(0, Math.floor(q / span)))
			if (seg?.k !== k) {
				seg = key(k)
				if (!seg) {
					dead = true
					return
				}
				warm(k + 1)
			}
			setShaderFieldMorphProgress(node, (q - seg.s0) / (seg.s1 - seg.s0))
		}
		;(child._fillMorphs ??= []).push(drive)

		this._registerSettle(child, ({ carry }) => {
			dead = true
			if (node.destroyed) return
			settleShaderFieldMorph(node, { carry })
			// Re-placed at the live outline now, so the successor opens on what is drawn.
			if (carry) followFill(node, shape, window.pixi_objects_map)
		})
	}

	/** This shape's dissolve an interrupted fade handed over, taken so nothing else continues it. */
	_takeFillCarry(child, id) {
		const carried = child._fillCarry?.get(id)
		child._fillCarry?.delete(id)
		return carried
	}

	/** Every handed-over dissolve no fill change took: it finishes on this fade's clock, or drops when its shape no longer shows its node. */
	_continueFills(child, vars) {
		const pixiMap = window.pixi_objects_map
		for (const [id, c] of child._fillCarry ?? []) {
			const entry = pixiMap?.get(id)
			if (entry && entry.fill === c.next)
				this._dissolveFill(child, id, entry, c.next, c.next?._fade ?? 1, c.olds, vars)
			else dropFillCarry(c)
		}
		child._fillCarry = null
	}

	// The outgoing nodes belong to no preset, so a terminal exit drops them. A carry hands an unfinished dissolve to the successor at the levels on screen.
	_settleFillCrossfades(child, { carry = false } = {}) {
		for (const fade of child._fillCrossfades ?? []) {
			const { id, next, olds } = fade
			const live = olds.filter(o => !o.node.destroyed && o.node._fade > 0)
			for (const o of olds) if (!live.includes(o)) dropOld(o)
			const rising = next && !next.destroyed && next._fade < 1
			if (carry && (rising || live.length)) {
				;(this._carriedFills ??= new Map()).set(id, { next, olds: live, colour: fade.colour?.() })
				continue
			}
			if (next && !next.destroyed) next._fade = undefined
			for (const o of live) dropOld(o)
		}
		child._fillCrossfades = null
	}

	// The fill nodes outside the map follow here: the dissolves and the created shapes'. `all` adds the map's, which otherwise follow on the ticker.
	_followFills(child, all = false) {
		const pixiMap = window.pixi_objects_map
		for (const fade of child._fillCrossfades ?? []) fade.apply()
		for (const { pixiObj, fill } of child._owned?.values() ?? []) if (fill) followFill(fill, pixiObj, pixiMap)
		if (all) for (const { pixiObj, fill } of pixiMap?.values() ?? []) if (fill) followFill(fill, pixiObj, pixiMap)
	}

	// A fill node built for a shape this fade creates: owned with it, followed each frame, handed over or dropped with it.
	_createFill(child, id, toObj, toMap, toState) {
		const owned = child._owned?.get(id)
		if (!owned || owned.fill || !fillBacking(toObj) || !window.pixi_sync_fill) return
		window.pixi_sync_fill(id, owned, toObj, { objects: toMap, bags: toState.source_settings })
	}

	_swapToDestinationTexture(child, id, pixiObj, toObj, fromObj = null) {
		const tex = this._pendingTextures?.get(toObj.source)
		if (!tex || tex.destroyed || pixiObj.destroyed) return
		const sx = Math.sign(pixiObj.scale.x) || 1
		const sy = Math.sign(pixiObj.scale.y) || 1
		const w = Math.abs(pixiObj.scale.x) * (pixiObj.texture?.width || 1)
		const h = Math.abs(pixiObj.scale.y) * (pixiObj.texture?.height || 1)
		this._settleList(child, '_textureSwaps', a => this._settleTextureSwaps(child, a)).push({
			id,
			pixiObj,
			// The only handle on the outgoing texture: `_pendingTextures` holds what the fade preloaded — its DESTINATION — so a settle back to the origin has nothing to look up.
			fromTexture: pixiObj.texture,
			fromSource: fromObj?.source ?? null,
		})
		pixiObj.texture = tex
		// ⚠️ Here, not only at the settle: the settle skips its own write whenever the sprite already shows the destination, which is every fade that simply lands.
		pixiObj._lastSource = resolveAssetUrlSync(toObj.source)
		pixiObj.scale.x = (sx * w) / (tex.width || 1)
		pixiObj.scale.y = (sy * h) / (tex.height || 1)
	}

	// The swap writes `texture` past the `_lastSource` guard React idempotence-checks against, so React never re-applies the source itself and whatever a fade leaves on the sprite is final. A fade ending anywhere but its destination — a reverse, a kill — is exactly when that is the wrong texture.
	//
	// ⚠️ `_lastSource` must be written to match, or React's next source change is a no-op against a guard naming the texture this replaced.
	_settleTextureSwaps(child, { owns = null } = {}) {
		if (!child._textureSwaps) return
		const objects = owns ?? store.getState().live?.objects ?? null
		for (const { id, pixiObj, fromTexture, fromSource } of child._textureSwaps) {
			if (pixiObj.destroyed) continue
			const source = objects?.[id]?.source
			const tex = source === fromSource ? fromTexture : source ? this._pendingTextures?.get(source) : null
			if (!tex || tex.destroyed || pixiObj.texture === tex) continue
			const sx = Math.sign(pixiObj.scale.x) || 1
			const sy = Math.sign(pixiObj.scale.y) || 1
			const w = Math.abs(pixiObj.scale.x) * (pixiObj.texture?.width || 1)
			const h = Math.abs(pixiObj.scale.y) * (pixiObj.texture?.height || 1)
			pixiObj.texture = tex
			pixiObj.scale.x = (sx * w) / (tex.width || 1)
			pixiObj.scale.y = (sy * h) / (tex.height || 1)
			pixiObj._lastSource = resolveAssetUrlSync(source)
		}
		child._textureSwaps = null
	}

	_syncGhosts(child) {
		for (const { ghost, pixiObj, follow } of child._ghostCrossfades ?? []) {
			// An unloaded sprite's 1×1 box would shrink the ghost to a dot.
			if (ghost.destroyed || pixiObj.destroyed || pixiObj.texture === Texture.EMPTY) continue
			// ⚠️ Measure UNMASKED: a mask crossing installs a stencil on the subject mid-fade, and `getBounds()` then reports the clipped box — sizing the still to that squashes it a little more every frame.
			const b =
				follow && !follow.anchor.destroyed
					? followBox(follow, unmaskedBounds(follow.anchor))
					: unmaskedBounds(pixiObj)
			const w = b.width ?? b.maxX - b.minX
			const h = b.height ?? b.maxY - b.minY
			if (!w || !h) continue
			const gx = signOf(ghost.scale.x)
			const gy = signOf(ghost.scale.y)
			ghost.scale.x = (w / (ghost.texture?.width || 1)) * gx
			ghost.scale.y = (h / (ghost.texture?.height || 1)) * gy
			ghost.x = (b.x ?? b.minX) + (gx < 0 ? w : 0)
			ghost.y = (b.y ?? b.minY) + (gy < 0 ? h : 0)
		}
	}

	/** ⚠️ Each ghost holds the FROM order while its object takes the TO order — that dissolving occlusion boundary IS the crossfade. Above its own object instead, the two are one picture and a reorder still pops. */
	_placeGhosts(child, fromMap) {
		const stage = window.pixi_app?.stage
		if (!stage) return
		const order = fromMap.get('root')?.children ?? []
		// Bottom-up: each `setChildIndex` shifts everything above it.
		const seq = (child._ghostCrossfades ?? [])
			.filter(g => !g.ghost.destroyed && !g.pixiObj.destroyed && g.ghost.parent === stage)
			.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
		// Below every object's node, fill included, for a ghost with nothing under it in the FROM order.
		let floor = -1
		for (const oid of order) {
			const e = window.pixi_objects_map?.get(oid)
			for (const n of [e?.fill, e?.pixiObj ?? this._ownedPixiObj(oid, child)]) {
				const i = n?.parent === stage ? stage.children.indexOf(n) : -1
				if (i >= 0 && (floor < 0 || i < floor)) floor = i
			}
		}
		for (const { ghost, pixiObj, id } of seq) {
			const below = order.indexOf(id) < 0 ? pixiObj : this._ghostAnchor(id, order, stage, child)
			const at = below ? stage.children.indexOf(below) + 1 : floor
			if (at >= 0) stage.setChildIndex(ghost, Math.min(at, stage.children.length - 1))
		}
	}

	/** The object the FROM order puts directly below this one, and so the ghost's floor. */
	_ghostAnchor(id, order, stage, child) {
		for (let i = order.indexOf(id) - 1; i >= 0; i--) {
			const p = window.pixi_objects_map?.get(order[i])?.pixiObj ?? this._ownedPixiObj(order[i], child)
			// A hidden object draws nothing, and its slot is no floor.
			if (p && p.parent === stage && p.visible) return p
		}
		return null
	}

	/**
	 * Hold a group child's RENDERED position across its parent's mirror flip.
	 *
	 * ⚠️ Solved per frame: the child's x/y is group-LOCAL and the group's extent tweens, so no one-time shift holds.
	 */
	_driveChildAcrossMirror({ child, pixiObj, fromObj, toObj, fromMap, toMap, vars }) {
		if (!pixiObj) return false
		const parentId = (fromObj ?? toObj)?.parentId
		if (!parentId) return false
		const pFrom = fromMap.get(parentId)
		const pTo = toMap.get(parentId)
		if (!pFrom || !pTo || (pFrom.type !== 'group' && pTo.type !== 'group')) return false
		if (!mirrorFlipped(pFrom, pTo)) return false
		if (fromObj && toObj && fromObj.parentId !== toObj.parentId) return false

		const fromW = fromObj ? getStoredChildWorldBounds(fromObj, Object.fromEntries(fromMap)) : null
		const toW = toObj ? getStoredChildWorldBounds(toObj, Object.fromEntries(toMap)) : null
		const a = fromW ?? toW
		const b = toW ?? fromW
		if (!a || !b) return false

		// Marks the object so the generic per-key tween leaves x/y alone — ordering between the two is not guaranteed, and whichever runs second would win.
		pixiObj._mirrorPositionOwned = true
		const entry = { t: 0 }
		entry.place = () => {
			if (pixiObj.destroyed) return
			const lb = pixiObj.getLocalBounds?.()
			if (!lb) return
			const wx = a.x + (b.x - a.x) * entry.t
			const wy = a.y + (b.y - a.y) * entry.t
			const parentObj = window.pixi_objects_map?.get(parentId)?.pixiObj ?? this._ownedPixiObj(parentId, child)
			// ⚠️ A child the transition created lives flat on the STAGE with the group's mirror already composed onto ITSELF, so the sign to invert is its own. One still inside the Container inherits the group's instead.
			const inGroup = parentObj && pixiObj.parent === parentObj
			const gx = inGroup ? parentObj.x : 0
			const gy = inGroup ? parentObj.y : 0
			const gsx = inGroup ? parentObj.scale.x || 1 : 1
			const gsy = inGroup ? parentObj.scale.y || 1 : 1
			const ownSX = inGroup ? 1 : pixiObj.scale.x || 1
			const ownSY = inGroup ? 1 : pixiObj.scale.y || 1
			const farX = gsx * ownSX < 0
			const farY = gsy * ownSY < 0
			// Local bounds exclude the object's own pivot, which its transform subtracts.
			const px = pixiObj.pivot?.x ?? 0
			const py = pixiObj.pivot?.y ?? 0
			pixiObj.x = (wx - gx) / gsx - ((farX ? lb.x + lb.width : lb.x) - px) * Math.sign(ownSX)
			pixiObj.y = (wy - gy) / gsy - ((farY ? lb.y + lb.height : lb.y) - py) * Math.sign(ownSY)
		}
		entry.release = () => {
			pixiObj._mirrorPositionOwned = false
		}
		this._registerMirrorPosition(child, entry, vars)
		return true
	}

	// ⚠️ `onUpdate` alone misses both ends — GSAP does not fire it at exactly t=0 or t=1, which is where a scrubbed fade is judged.
	_registerMirrorPosition(child, entry, vars) {
		this._settleList(child, '_mirrorPositions', () => {
			for (const e of child._mirrorPositions ?? []) e.release?.()
			child._mirrorPositions = null
		}).push(entry)
		child.tl.to(entry, { ...vars, t: 1, onUpdate: entry.place }, 0)
		entry.place()
	}

	// The ghost belongs to no preset, so both exits destroy it — a successor freezes its own from the live object.
	_settleGhostCrossfades(child) {
		if (!child._ghostCrossfades) return
		for (const { ghost, ownsTexture } of child._ghostCrossfades) {
			if (ghost.destroyed) continue
			ghost.parent?.removeChild(ghost)
			ghost.destroy({ texture: !!ownsTexture })
		}
		child._ghostCrossfades = null
	}

	// `updatePixiObject` writes a vector's scale to its draw scale, never `pixiObj.scale`, so an inherited scale a crosser composed there is ours to undo at both exits.
	_settleVectorScales(child) {
		if (!child._vectorScaleRestores) return
		for (const { pixiObj } of child._vectorScaleRestores) {
			if (pixiObj.destroyed) continue
			pixiObj.scale.x = 1
			pixiObj.scale.y = 1
		}
		child._vectorScaleRestores = null
	}

	/**
	 * Snapshot current animated values for use as fromState in a new transition.
	 *
	 * ⚠️ `omitMorphs` is for the SETTLE, not the interrupt. The morph overlay below coerces every morphing param to its DESTINATION, which is what a successor fade wants as a clean endpoint — and is a hard cut for a park, where the fade owning the key is still live and republishes nothing. A morph key must never be NAMED in a settle patch while its fade runs; omitted, `patchLiveObject` leaves the envelope the per-frame dispatch already wrote alone.
	 */
	getActiveState({ omitMorphs = false } = {}) {
		// ⚠️ NULL on a hold, so the caller falls through to `live`. A hold child holds no fade state at all, and the delegating accessors would hand back an object of `undefined`s — truthy, and used as a fromState that names nothing.
		if (this._child()?.kind !== 'fade') return null
		const state = {}

		// Every effect: full fromState, overlaid with the tweened keys from its proxy. An effect missing here snapshots its PRE-fade value, so re-triggering a transition mid-fade would visibly jump those params back.
		for (const effect of TWEEN_EFFECTS) {
			const target = this._effectTargets?.[effect]
			// Absence is PRESERVED, not materialized — except while this transition is tweening the effect INTO existence, where the proxy holds the only mid-flight values and dropping them restarts the sweep on interrupt.
			if (!this._fromState?.[effect] && !target) continue
			state[effect] = { ...this._fromState?.[effect] }
			if (target) {
				const { _gsap, ...tweened } = target
				Object.assign(state[effect], tweened)
				const snapConfig = getSnapConfig(effect)
				for (const key in snapConfig) {
					if (key in state[effect] && typeof state[effect][key] === 'number') {
						state[effect][key] = Math.round(state[effect][key])
					}
				}
			}
		}

		// A morphing param has NO snapshottable intermediate: the server interpolates it in embedding space, and the client only ever held the two endpoints. Snapshot the DESTINATION so a re-triggered transition starts from a clean endpoint rather than from an envelope.
		//
		// This value is NOT the successor's start point — an omitted `a` makes the server anchor at the live blend instead (see `morphEnvelope`), so the interior point the client cannot name never needs to appear here. What it still feeds is the successor's CHANGE TEST, where being the old destination reads as "unchanged" on a reversal — which is why `createTransition` carries the mid-morph keys across as a separate admission signal.
		for (const qualified in this._morphProxies ?? {}) {
			const dot = qualified.indexOf('.')
			const effect = qualified.slice(0, dot)
			const key = qualified.slice(dot + 1)
			if (!state[effect]) continue
			// ⚠️ DELETED for a settle, not left alone: `_fromState` already put the PRE-fade value in `state[effect]`, so merely skipping the overlay names the key with the value the fade departed from — a hard cut backwards instead of forwards.
			if (omitMorphs) delete state[effect][key]
			else state[effect][key] = this._toState?.[effect]?.[key] ?? this._fromState?.[effect]?.[key]
		}

		// Objects — merge from toState structure, override with current PixiJS values
		if (this._fromState?.objects || this._toState?.objects) {
			const fromObjs = this._fromState?.objects ?? {}
			const toObjs = this._toState?.objects ?? {}
			const allIds = new Set([...Object.keys(fromObjs), ...Object.keys(toObjs)])
			const objects = {}
			// Mid-retype the real id is still the OLD pixiObj; its replacement is carried under the synthetic id.
			const retyped = new Set([...(this._owned?.values() ?? [])].map(e => e.realId).filter(Boolean))

			for (const id of allIds) {
				if (id === 'root') continue
				const entry = window.pixi_objects_map?.get(id)
				// An object still fading in is owned by the transition, not yet in pixi_objects_map, so fall back to the ownership registry — otherwise this snapshots the destination at FULL opacity while the object is on screen at a partial alpha, and the next transition restarts its fade from 0.
				const pixiObj = entry?.pixiObj ?? this._ownedPixiObj(id)
				const baseObj = (retyped.has(id) && fromObjs[id]) || toObjs[id] || fromObjs[id]
				if (!baseObj) continue

				// ⚠️ A FROM-ONLY id survives only while THIS fade owns it. `_fromState` is itself a previous snapshot, so trusting it accumulates every interrupted fade's objects into a `live` no preset declares.
				if (!toObjs[id] && !this._ownedPixiObj(id)) continue

				if (!pixiObj) {
					// Genuinely not on stage — use toState values
					objects[id] = { ...baseObj }
					continue
				}

				// Copy non-animated props from the base object, then override with current animated PixiJS values.
				const snap = { ...baseObj }
				const objType = baseObj.type
				snap.x = pixiObj.x
				snap.y = pixiObj.y
				snap.rotation = pixiObj.rotation * (180 / Math.PI)
				// An image's rendered scale is `scaleX * width / texW`, so reading it back would store the size TWICE — once here and again in `width`, which the next transition would multiply together. Its Redux scaleXY is pure sign and only the tween below moves it, so `baseObj`'s value is already the right answer.
				if (VECTOR_TYPES.has(objType) && pixiObj.setDrawPose) {
					snap.scaleX = pixiObj.scale.x * pixiObj.scaleX
					snap.scaleY = pixiObj.scale.y * pixiObj.scaleY
				} else if (objType !== 'image') {
					snap.scaleX = pixiObj.scale?.x ?? pixiObj.scaleX
					snap.scaleY = pixiObj.scale?.y ?? pixiObj.scaleY
				}
				snap.opacity = pixiObj.opacity ?? pixiObj.alpha * 100
				// Mid-crossing: `visible` was forced true for the fade and the object is rendering at a partial alpha (captured just above). baseObj is the DESTINATION, which for a fade-out says visible=false — snapshotting that would make the next transition read this as hidden and restart the fade from 0. Reads the crossing registry, so this must run BEFORE kill() clears it (loadPreset calls getActiveState() first — keep that order).
				if (this._visibilityCrossings?.some(c => c.id === id)) snap.visible = true
				// Mid-mask-crossing: baseObj is the DESTINATION, so `mask` already reads as the finished link and the successor would see from===to, skip the crossing, and let React snap the mask on at completion — the original pop, reintroduced on every interrupt. Snapshot the link as it currently RENDERS: coverage still mostly uniform (rect alpha high) means the eye is still seeing the unmasked side, so report that side. The successor then rebuilds a crossing from the correct end. Partial coverage is not representable in Redux's boolean-ish link, so this is a threshold, not a lerp — at the midpoint either answer is equally wrong and the re-fade is short.
				const mc = this._maskCrossings?.find(c => c.id === id)
				if (mc && !mc.rect.destroyed) {
					// Coverage is the rect's AREA, not its alpha: still near stage-wide renders as UNMASKED, closed onto the stencil as fully masked.
					const linkedId = (mc.masking ? toObjs[id]?.mask : fromObjs[id]?.mask) || null
					const b = mc.rect.getBounds?.()
					const stage = window.pixi_app?.screen
					const spread =
						b && stage?.width ? ((b.width ?? 0) * (b.height ?? 0)) / (stage.width * stage.height) : 1
					snap.mask = spread > 0.5 ? null : linkedId
				}
				// Group-boundary coherence: createTransition reparents a group⇄ungroup crosser to the stage and writes WORLD x/y/scale onto the pixiObj. If baseObj (toState) still describes it as a group child (parentId → group, group-local frame), the world x/y we just read would be misread as group-local on the next transition. Since the pixiObj physically lives on the stage, snapshot it as top-level so the frame matches the values.
				if (baseObj.parentId && baseObj.parentId !== 'root' && pixiObj.parent === window.pixi_app?.stage) {
					snap.parentId = 'root'
				}
				// Scale-realized size (image/group): if a scale tween is active, reconstruct the interpolated size so the snapshot is coherent — a snapshot with destination width + mid-tween scale would jump on the next transition. (For group, the scaleX/scaleY override above already suffices, but carrying width/height keeps the metadata coherent.)
				if (pixiObj._scaleTransitionProxy) {
					const st = pixiObj._scaleTransitionProxy.t
					const fromO = fromObjs[id]
					const toO = toObjs[id]
					if (fromO && toO) {
						if (objType === 'image') {
							// The image tween interpolates the SIGNED product `scaleX * width`, so split it back the way it is stored. The scale keeps its own magnitude (an SVG import can carry one) and contributes only the sign; whatever is left of the product is the width. Mid-flip the product crosses zero, where `Math.sign` is 0 — an unrecoverable scale — so at exactly zero the destination sign stands in.
							const lerp = (a, b) => a + (b - a) * st
							const px = lerp((fromO.scaleX ?? 1) * fromO.width, (toO.scaleX ?? 1) * toO.width)
							const py = lerp((fromO.scaleY ?? 1) * fromO.height, (toO.scaleY ?? 1) * toO.height)
							const magX = Math.abs(lerp(Math.abs(fromO.scaleX ?? 1), Math.abs(toO.scaleX ?? 1))) || 1
							const magY = Math.abs(lerp(Math.abs(fromO.scaleY ?? 1), Math.abs(toO.scaleY ?? 1))) || 1
							snap.scaleX = (px === 0 ? Math.sign(toO.scaleX ?? 1) : Math.sign(px)) * magX
							snap.scaleY = (py === 0 ? Math.sign(toO.scaleY ?? 1) : Math.sign(py)) * magY
							snap.width = Math.abs(px) / magX
							snap.height = Math.abs(py) / magY
						} else {
							if (fromO.width !== undefined && toO.width !== undefined) {
								snap.width = fromO.width + (toO.width - fromO.width) * st
							}
							if (fromO.height !== undefined && toO.height !== undefined) {
								snap.height = fromO.height + (toO.height - fromO.height) * st
							}
						}
					}
				}
				// A source coming in snapshots as itself: its node and the colour under it carry over (`_carriedFills`). A colour coming in snapshots at its lerp, the source going out carried beside it.
				const fromFill = fromObjs[id]?.fill
				const toFill = toObjs[id]?.fill
				const fillProxy = pixiObj._fillTransitionProxy
				if (isSourceFill(toFill)) {
					snap.fill = toFill
				} else if (fillProxy?.pair) {
					snap.fill = fillProxy.pair.at(fillProxy.t)
				} else if (isSourceFill(fromFill)) {
					if (toFill !== undefined) snap.fill = toFill
				} else if (pixiObj.fillColor !== undefined) {
					snap.fill = pixiObj.fillColor
				}
				if (pixiObj.strokeColor !== undefined) snap.stroke = pixiObj.strokeColor
				objects[id] = snap
			}

			// Root background fill
			const fromRoot = fromObjs['root']
			const toRoot = toObjs['root']
			if (fromRoot || toRoot) {
				const baseRoot = toRoot ?? fromRoot
				const rootSnap = { ...baseRoot }
				if (this._bgFillProxy) {
					const fromFill = fromRoot?.fill
					const toFill = toRoot?.fill
					if (fromFill !== undefined || toFill !== undefined) {
						const pair = prepareFillPair(fromFill, toFill)
						if (pair) rootSnap.fill = pair.at(this._bgFillProxy.t)
					}
				}
				objects['root'] = rootSnap
			}

			// ⚠️ Parentage is stored TWICE, and each object here is copied from whichever endpoint holds it, so the halves can name different presets. `parentId` wins; `children` is rebuilt from it.
			for (const id in objects) {
				if (objects[id].children) objects[id] = { ...objects[id], children: [] }
			}
			for (const id in objects) {
				if (id === 'root') continue
				const parent = objects[objects[id].parentId ?? 'root'] ?? objects['root']
				if (parent?.children) parent.children.push(id)
			}

			state.objects = objects
		}

		// A source on one end keeps that end's bag; on both, the blend on screen.
		const bags = { ...this._toState?.source_settings, ...this._fromState?.source_settings }
		if (this._sourceBlend) {
			const { proxy, sources } = this._sourceBlend
			for (const source in sources) bags[source] = blendSource(sources[source], proxy.t)
		}
		// A path snapshots under B the ids B's bag sets; the rest of its vector rides the node to a successor.
		for (const r of this._pathFills ?? []) {
			const free = heldIds(r.b, r.toBag)
			bags[r.source] = { ...bags[r.source], ...Object.fromEntries(free.map(k => [k, r.values[k]])) }
		}
		if (Object.keys(bags).length) state.source_settings = bags

		// Copy any other top-level keys from fromState (effects, etc.)
		if (this._fromState) {
			for (const key of Object.keys(this._fromState)) {
				if (!(key in state)) state[key] = this._fromState[key]
			}
		}

		return state
	}

	/**
	 * Stop the animation and resolve every object it owns.
	 *
	 * @param nextObjects  objects map of the state taking over (createTransition passes its own
	 *                     toState). Omit for a standalone kill — ownership then resolves against Redux `live`, which is always authoritative. There is no "trust me, a transition is coming" mode: instant loads and unmounts broke that.
	 */
	kill(nextObjects = null, carry = false) {
		// ⚠️ The GSAP timeline dies here, but the CHILD RECORD outlives it to the end of this method: `_settleScene` below reads its settle registry, and the ownership it carries is what the successor adopts.
		const child = this._child()
		const tl = this.timeline
		if (tl) tl.kill()
		// Undo everything this fade put on the stage. An interrupt's successor is BOTH what owned objects resolve against and what may keep animating them; a bare kill has neither and falls through to Redux. The mask container is torn down regardless — the successor rebuilds its own from the state it snapshots.
		this._settleScene(child, { owns: nextObjects, successor: nextObjects, carry })
		// ⚠️ A park's handoff nothing came for — `parkRun` leaves the flag raised for a successor a hard cut never opens. Only with NO child: a killed child's own carry belongs to the successor, assigned below.
		if (!child && !carry) {
			if (this._carried) this._releaseOwnership({ _owned: this._carried }, nextObjects, false)
			clearPixiTransitionProxies()
			window.pixi_transition_active = false
		}
		// A park's dissolves and paths, which no successor came for.
		if (!carry && this._carriedFills) {
			for (const c of this._carriedFills.values()) dropFillCarry(c)
			this._carriedFills = null
		}
		if (!carry) settleCarriedPaths(null)
		// ⚠️ AFTER the settles above, and never on a carry — a successor tweens these from the pose they hold.
		if (!carry) resyncPixiFromLive()
		this._bgFillProxy = null
		this._sourceBlend = null
		// A killed fade settles nothing; the next transition opens its own gate.
		this._settling = false
		// Each closes over a pixiObj this fade owned; keeping them past the kill would drive destroyed objects on the next scrub.
		this._effectTargets = null
		// Progress proxies for the morphing params, keyed by qualified name. GSAP animates each `t`; the server owns the interpolation between the endpoints.
		this._morphProxies = null
		this._fromState = null
		this._toState = null
		// What `_releaseOwnership` kept, handed to the successor `createTransition` builds. Only a CHILD's — with none, `_owned` delegates to null and the write would drop a carry an earlier kill left for a successor still to come.
		//
		// ⚠️ Dropping the record is the LAST write: a per-fade assignment after this point would open a fresh child to hold it.
		if (child) this._carried = this._owned
		// The whole run goes with the fade: an orphaned parent would keep ticking toward children whose scenes nothing is building. `parkRun` is the one path that keeps a plan alive across an interrupt, and it takes the parent off this manager before any of this runs.
		//
		// ⚠️ A PARKED parent dies here too — but NOT when a successor is being built. `carry` is exactly that distinction: `createTransition` calls this to open the interrupt the park was made FOR, and dropping the plan there would defeat the parking entirely. Every other kill is a real ending, after which nothing would ever reach the parked parent again: a manual load leaves `playing` false, so no `startRun` follows to replace it.
		if (!carry) this.dropParked()
		this._parent?.kill()
		this._parent = null
		this._children = []
		this._at = 0
		// ⚠️ The dead run's callbacks go with it. Left behind, a later STANDALONE fade detached by a gesture would arm itself with the old plan's settle and dispatch that plan's destination preset.
		if (!carry) this._runCallbacks = null
		// Reconverge point: every field returns to its plain live value (setLiveObject has already snapped each to its clean destination on complete).
		if (Object.keys(store.getState().playback.morphs ?? {}).length) store.dispatch(clearMorph())
		if (store.getState().playback.transition_paused) store.dispatch(setTransitionPaused(false))
	}

	// Objects the transition owns but that are not yet in pixi_objects_map (created for this fade). Used by getActiveState and the fade-in image loader to find them.
	_ownedPixiObj(id, child = this._child()) {
		const e = child?._owned?.get(id)
		return e && !e.pixiObj.destroyed ? e.pixiObj : undefined
	}

	// Place a fading-in object at its intended stack depth on the stage.
	//
	// Reaches the same rendered order as React's z-order pass (usePixiObjects), which indexes by the object's position in the state's key order. Only root-level objects are counted — a grouped child renders flat on the stage for the duration of the transition (never parented into its group Container), so it has no sibling index to match and is left on top rather than guessed at.
	//
	// Order the stage to match the destination preset, in ONE pass after every object exists.
	//
	// Must run after all fade-ins are created: placing each object as it is built computes an index against a stage that does not yet contain its siblings, so every index lands short and the set ends up in creation order — an object visibly fades in at the wrong depth and jumps when React reconciles on complete.
	//
	// Authority is `root.children` (the same list React's z-order pass indexes by), NOT object key order, which is arbitrary.
	//
	// A destination group child that is flat on the stage (a crosser, or a fade-in) is placed BELOW its group's slot, in `group.children` order. Below, because the group's other children are still inside the Container and therefore render above it; placing the flat one after the group inverts them for the whole fade. ⚠️ Flat on the stage it cannot interleave with those in-container siblings at all — only above or below the group as a whole — which stays a limitation of rendering outside the Container.
	// ⚠️ Ordering is RELATIVE TO THE SURVIVORS, and a disappearing object holds the layer the FROM frame gave it — it fades to alpha 0 either way, so any depth change the eye can see there is a jump paid for nothing.
	_applyStageOrder(child, toMap, toStencils = null, fromMap = null, fromStencils = null) {
		const stage = window.pixi_app?.stage
		if (!stage) return
		const order = toMap.get('root')?.children
		if (!order) return
		// ⚠️ Listed in `root.children` is not SURVIVING: a hidden object is still listed, and ordering it by the destination drags a still-opaque fade-out to a depth the eye never saw it at.
		const survives = obj => isShownIn(obj, toMap, toStencils)
		// The destination sequence, flattened the way the stage actually renders it during a fade: each group preceded by whichever of its children are flat on the stage.
		const wanted = []
		for (const id of order) {
			const obj = toMap.get(id)
			if (!obj || (obj.parentId && obj.parentId !== 'root')) continue
			if (obj.type === 'group' && obj.children?.length) {
				for (const childId of obj.children) {
					// One level, matching resolveWorldTransform — a nested group's own children are not walked.
					if (toMap.get(childId)?.type === 'group') continue
					wanted.push(childId)
				}
			}
			wanted.push(id)
		}
		// A departing object holds the depth the FROM frame gave it: it fades where the eye last saw it, above whatever it was covering.
		const departing = wanted.filter(id => !survives(toMap.get(id)))
		for (const id of departing) wanted.splice(wanted.indexOf(id), 1)
		wanted.push(...departing)

		// ⚠️ A FROM-ONLY FADE-OUT MUST PARTICIPATE. Slots are reassigned by absolute index, so omitting it preserves the index it holds and not its layering — a survivor taking a lower slot slides under it.
		const fromOrder = fromMap?.get('root')?.children
		if (fromOrder) {
			const flatFrom = []
			for (const id of fromOrder) {
				const obj = fromMap.get(id)
				if (!obj || (obj.parentId && obj.parentId !== 'root')) continue
				if (obj.type === 'group' && obj.children?.length)
					for (const childId of obj.children) {
						if (fromMap.get(childId)?.type === 'group') continue
						flatFrom.push(childId)
					}
				flatFrom.push(id)
			}
			for (let i = 0; i < flatFrom.length; i++) {
				const id = flatFrom[i]
				if (toMap.has(id) || wanted.includes(id)) continue
				if (!isShownIn(fromMap.get(id), fromMap, fromStencils)) continue
				// Directly above the nearest thing below it in the FROM frame the destination still DRAWS. A non-survivor is no anchor: already moved to the departing tail, it would land this on top of everything.
				let at = 0
				for (let j = i - 1; j >= 0; j--) {
					const belowId = flatFrom[j]
					if (!survives(toMap.get(belowId))) continue
					const below = wanted.indexOf(belowId)
					if (below >= 0) {
						at = below + 1
						break
					}
				}
				wanted.splice(at, 0, id)
			}
		}

		// PERMUTE WITHIN THE SLOTS THE SURVIVORS ALREADY HOLD, rather than packing them into a run from the bottom. Packing assigns consecutive indices, which silently evicts every stage child that is NOT a survivor — a fading-out object, and the bg — to above the packed run: the disappearing object ends up rendering on top of the whole scene for the fade. Reassigning the same slot set leaves every non-participant exactly where it is, and needs no bg offset because the bg is simply not in `wanted`.
		const objs = wanted
			.map(id => window.pixi_objects_map?.get(id)?.pixiObj ?? this._ownedPixiObj(id, child))
			.filter(p => p && p.parent === stage)
		if (objs.length < 2) return
		const slots = objs.map(p => stage.children.indexOf(p)).sort((a, b) => a - b)
		for (let i = 0; i < objs.length; i++) {
			// An already-ordered set matches slot for slot, so this is the no-op an explicit pre-check used to buy.
			if (stage.children.indexOf(objs[i]) !== slots[i]) stage.setChildIndex(objs[i], slots[i])
		}
	}

	/**
	 * Carry Pixi's pivot from the FROM object's to the TO object's. A vector's pivot is a fraction of its `data` extent, and a morph changes that extent, so a pivot held at the FROM value jumps when React applies the destination on completion. An image's anchor moves only with `pivotX`/`pivotY`. A crossing pins the visible side's pose, pivot included (`updatePixiObject` set it).
	 */
	_tweenPivot(child, pixiObj, objType, fromObj, toObj, vars, crossing) {
		if (crossing || !pixiObj || pixiObj.destroyed) return
		if (objType === 'image' && pixiObj.anchor) {
			const ax = toObj.pivotX ?? 0.5
			const ay = toObj.pivotY ?? 0.5
			if (ax !== pixiObj.anchor.x || ay !== pixiObj.anchor.y)
				child.tl.to(pixiObj.anchor, { ...vars, x: ax, y: ay }, 0)
			return
		}
		if (!VECTOR_TYPES.has(objType) || !pixiObj.setDrawPose) return
		const to = localPivot(toObj)
		const from = { ...pixiObj._unscaledPivot }
		if (to.x === from.x && to.y === from.y) return
		// In unscaled `data` units: the draw scale sweeps beside it, and `setDrawPose` scales the pivot with it.
		const pivot = { ...from }
		child.tl.to(
			pivot,
			{
				...vars,
				x: to.x,
				y: to.y,
				onUpdate: () => pixiObj.setDrawPose({ ...pivot }, pixiObj.scaleX, pixiObj.scaleY),
			},
			0
		)
	}

	// Load + size the texture for an image that is fading in (created with Texture.EMPTY). Async: the transition may kill() before it resolves, so re-check the sprite is still a tracked fade-in object before touching it. Never writes `alpha` — the GSAP fade owns it. `worldT` (optional) carries the group world-scale for an image inside a scaled group — image scale is realized as size/texture, so it must be multiplied by the group scale on top.
	_loadFadeInImageTexture(child, id, pixiObj, toObj, worldT = null) {
		const load = loadImageTextureFor(toObj.source)
		if (!load) return
		load.then(tex => {
			// Still fading this exact sprite in? Ownership survives an interrupt handoff, so a carried-over image keeps loading instead of silently fading in with no pixels.
			if (this._ownedPixiObj(id, child) !== pixiObj) return
			pixiObj.texture = tex
			const texW = tex.width || 1
			const texH = tex.height || 1
			const { width = 100, height = 60, scaleX = 1, scaleY = 1 } = toObj
			const gsx = worldT?.scaleX ?? 1
			const gsy = worldT?.scaleY ?? 1
			// The anchor is on the pivot, so a mirror is in place: no offset.
			pixiObj.scale.x = (scaleX * width * gsx) / texW
			pixiObj.scale.y = (scaleY * height * gsy) / texH
			// The pixels arrive off-frame, and nothing on the fade path renders — so this one does.
			try {
				window.pixi_app?.renderer.render(window.pixi_app.stage)
			} catch (e) {
				logger.warn('render error loading fade-in image texture', e)
			}
		}).catch(e => logger.warn('fade-in image texture load error', toObj.source, e))
	}

	// Add a previously-live prop to the active tween so it catches up to toState. propKey is a dot-prop path: 'diffusion.strength' or 'filter.blur'.
	joinTransition(propKey) {
		if (!this.timeline || !this._toState) return

		const elapsed = this.timeline.time()
		const total = this.timeline.duration()
		const remaining = Math.max(total - elapsed, 0)
		if (remaining <= 0) return

		// One path for every effect. This was two near-identical branches covering `diffusion` and `filter` only, so un-pinning a param of any other effect mid-fade did nothing at all — the param sat at its live value until the fade ended.
		const dot = propKey.indexOf('.')
		if (dot < 0) return
		const effect = propKey.slice(0, dot)
		const key = propKey.slice(dot + 1)
		if (!TWEEN_EFFECTS.includes(effect)) return

		const toValue = this._toState[effect]?.[key]
		if (toValue === undefined) return

		// A `morph: true` param rejoins as a MORPH, not as a numeric tween and not as a jump: it gets its own progress proxy over the remaining time, exactly like the initial build, and the server interpolates from what it is showing to `b`. This is what an omitted `a` buys — the param was pinned when the timeline was built, so no morph was ever started for it and there is no `a` any string could name. Handing the string to GSAP instead would tween toward a non-number and blank the param.
		//
		// Positioned at `elapsed` with `duration: remaining` so it lands with everything else. Registered in `_morphProxies` so `getActiveState` snapshots it and `kill()` clears it like any other morph.
		if (EFFECT_SCHEMAS[effect]?.properties?.[key]?.morph) {
			if (this._morphProxies?.[propKey]) return
			// No `from` for the same reason there is no `a`: nothing this param was showing has a name here, so the outgoing frame stays blank.
			const proxy = { t: 0, effect, key, b: toValue, from: '' }
			;(this._morphProxies ??= {})[propKey] = proxy
			store.dispatch(setMorph([propKey, morphDisplay(toValue, 0, '')]))
			this.timeline.to(
				proxy,
				{
					duration: remaining,
					// Linear, for the same reason the initial build is — see `morphEnvelope`'s note on progress vs. value.
					ease: 'none',
					t: 1,
					onUpdate: () => this._publishMorph(propKey),
				},
				elapsed
			)
			return
		}

		// A param that must not sweep catches up by JUMPING — the same rule the initial tween applies, both reading the contract's `tween` flag.
		if (jumpsRatherThanTweens(effect, key)) {
			store.dispatch({
				type: 'live/setLiveProperty',
				payload: [propKey, toValue],
				meta: { skipHistory: true },
			})
			return
		}

		let target = this._effectTargets?.[effect]

		if (!target) {
			target = {}
			if (!this._effectTargets) this._effectTargets = {}
			this._effectTargets[effect] = target
			const existingUpdate = this.timeline.eventCallback('onUpdate')
			this.timeline.eventCallback('onUpdate', () => {
				existingUpdate?.()
				this._publishBags({ [effect]: target })
			})
		}

		// ⚠️ The CHANNEL first: it holds the mid-fade value, while Redux holds the last settled one. Seeding a newly-unpinned prop from Redux would start it from where the fade began. Absence is at-initial, so the contract resolves it before `_fromState`.
		const live = paramChannel.has(`${effect}.${key}`)
			? paramChannel.get(`${effect}.${key}`)
			: EFFECT_SCHEMAS[effect]?.properties?.[key]?.initial
		const currentValue = live ?? this._fromState[effect]?.[key]
		if (!(key in target)) target[key] = currentValue
		if (target[key] === toValue) return

		const snapConfig = getSnapConfig(effect)
		const tweenVars = { [key]: toValue, duration: remaining, ease: 'none' }
		if (snapConfig[key]) tweenVars.snap = { [key]: snapConfig[key] }
		this.timeline.to(target, tweenVars, elapsed)
	}

	/** A fade opens running. */
	_openUnpaused() {
		if (store.getState().playback.transition_paused) store.dispatch(setTransitionPaused(false))
	}

	/**
	 * Publish one frame of tweened effect params.
	 *
	 * ⚠️ To the CHANNEL, not Redux. The per-frame dispatch is the storm; Redux is written once, when the gesture ends (`commitLiveFromTransition`, `complete()`, `abortTransition`).
	 *
	 * ⚠️ A PINNED prop is excluded here and nowhere else, read from the store each frame so a pin taken mid-fade lands on the next tick.
	 */
	_publishBags(bagTargets) {
		const pinned = selectLiveProps(store.getState())
		for (const effect in bagTargets) {
			const target = bagTargets[effect]
			for (const key in target) {
				// GSAP hangs its own bookkeeping on every target it tweens.
				if (key === '_gsap') continue
				const qualified = `${effect}.${key}`
				if (isPinned(pinned, qualified)) continue
				paramChannel.set(qualified, target[key])
			}
		}
	}

	// ⚠️ Channel only: Redux holds a morph's endpoints from `setMorph` to `clearMorph`, and its `t` has no store reader (MorphScroll draws off `getProgress()`).
	_publishMorph(qualified) {
		const proxy = this._morphProxies?.[qualified]
		if (!proxy) return
		if (isPinned(selectLiveProps(store.getState()), qualified)) return
		paramChannel.set(qualified, morphEnvelope(proxy.b, proxy.t, this._fadeId))
	}

	/**
	 * Gate the timeline's `onComplete` slot behind `_settling`, whoever registers it.
	 *
	 * ⚠️ The slot is owned by `loadPreset`, outside this class, and holds the fade's one-way settle. Wrapping the registrar is what keeps the rule here rather than in every caller.
	 */
	_gateCompletion(child) {
		const tl = child.tl
		const register = tl.eventCallback.bind(tl)
		tl.eventCallback = (...args) => {
			// The GETTER form (one argument) must reach GSAP untouched — the render chain reads the current callback through it to compose onto.
			const [type, fn] = args
			if (args.length < 2 || type !== 'onComplete' || typeof fn !== 'function') return register(...args)
			args[1] = (...a) => {
				if (!child._settling) return undefined
				const out = fn(...a)
				// After the caller's settle, so B's fill is in Redux and keeps them; its history step, no entry of its own.
				const landed = this.takeLandedEdits(child)
				if (landed.length)
					store.dispatch({ type: 'live/setLiveProperties', payload: landed, meta: { skipHistory: true } })
				return out
			}
			return register(...args)
		}
	}

	/**
	 * The params mid-morph when this interrupt arrived, which the incoming fade must keep morphing.
	 *
	 * ⚠️ An ADMISSION signal, not an anchoring one: `getActiveState` snapshots a morphing param to its DESTINATION, so reversing mid-fade hands `getEffectTweenProps` `fromVal === toVal` and the param would hold at the old destination while everything else sweeps back.
	 *
	 * ⚠️ ONLY a morph that was going somewhere carries. `from === b` interpolates a value to itself — admitting it re-arms the morph on every later fade, blocking save (`selectLiveInvalid`). A detached fade publishes `from: ''`, so it still carries.
	 *
	 * ⚠️ Each carries its DESTINATION, not just its name: admission also needs to know whether the new fade is heading somewhere else (`getEffectTweenProps`).
	 *
	 * Read `_morphProxies` BEFORE `kill()`, which nulls it.
	 */
	_carriedMorphs(child = this._child()) {
		const out = new Map()
		for (const [qualified, p] of Object.entries(child?._morphProxies ?? {}))
			if (p.from !== p.b) out.set(qualified, p.b)
		return out
	}

	createTransition(fromState, toState, duration, liveProps = []) {
		// Captured BEFORE kill() nulls `_morphProxies`. The object-side counterpart is `carriedIds` below.
		const carriedMorphs = this._carriedMorphs()

		// Resolve the outgoing transition's objects against THIS transition's destination:
		// anything it created that toState drops is destroyed now (no deferred callback that a further interrupt could discard). carry=true keeps the survivors OWNED — Redux will not contain them until this transition completes, and handing them to React early gets them destroyed mid-fade.
		this.kill(toState?.objects ?? null, true)

		// Objects still owned after the kill were mid-fade-in when we interrupted. They must not have their opacity reset to 0 below — they are already partway up.
		const carriedOwned = this._carried
		this._carried = null

		// ⚠️ AFTER `kill()`, which tears down the outgoing fade's proxies. Bumping earlier would stamp the new id on any final envelope the dying fade still emits.
		this._fadeId++

		// ⚠️ The child must exist BEFORE any per-fade field is written — those delegate to it, and a write with no child is dropped.
		this.timeline = gsap.timeline()
		this._build(this._child(), fromState, toState, duration, liveProps, carriedMorphs, carriedOwned)
		return this.timeline
	}

	// ⚠️ A failed build becomes an empty fade that lands as a cut, never a throw: the caller has already marked the fade in flight.
	_build(child, fromState, toState, duration, ...rest) {
		try {
			this._buildInto(child, fromState, toState, duration, ...rest)
		} catch (e) {
			logger.error('fade build failed; it lands as a cut', e)
			child.tl.clear()
			child.tl.eventCallback('onUpdate', null)
			this._settleScene(child)
			child._effectTargets = null
			child._morphProxies = null
			child._sourceBlend = null
			child._bgFillProxy = null
			if (Object.keys(store.getState().playback.morphs ?? {}).length) store.dispatch(clearMorph())
			clearPixiTransitionProxies()
			window.pixi_transition_active = false
			resyncPixiFromLive()
			child._settling = true
			child.tl.set({}, {}, duration)
			this._openUnpaused()
		}
	}

	/**
	 * Build one fade's tweens, settlers and callbacks INTO an existing child.
	 *
	 * Shared by the standalone path (`createTransition`, which opens its own child) and the run (`buildFade`, whose child the parent already owns and whose duration is already reserved).
	 */
	_buildInto(child, fromState, toState, duration, liveProps, carriedMorphs, carriedOwned) {
		const vars = { duration, ease: 'none' }
		const carriedIds = new Set(carriedOwned ? carriedOwned.keys() : [])
		// Ownership the outgoing fade handed over: these objects are still on the stage and this fade must keep animating them.
		child._owned = carriedOwned
		child._fillCarry = this._carriedFills
		this._carriedFills = null
		// ⚠️ ONCE, before both builders, so `_toState` IS the merged map — six consumers read it (the tween builders, `_releaseOwnership`, `_settleVisibilityCrossings`, `getActiveState`, `_collectInpaintEntries`) and four independent patches would drift.
		toState = this._mergeVocabPins(this._mergeObjectPins(toState, fromState, liveProps))
		// Store from/to state references for getActiveState()
		child._fromState = fromState
		child._toState = toState
		// OPEN by default: a fade that plays through settles itself. Only a scrub, which crosses the end whenever the user drags there, closes it.
		child._settling = true
		this._gateCompletion(child)
		window.pixi_transition_active = true

		// FIRST: owned objects resolve against the state taking over, before anything reads their flags.
		this._registerSettle(child, ({ owns, carry }) => this._releaseOwnership(child, owns, carry))

		// Loops blend between the two ends on this fade's own progress (automation plan A8); `_settleScene` ends it.
		getEngine()?.beginFade(toState, () => Math.min(1, Math.max(0, child.tl.progress())))

		this._buildEffectTweens(child, fromState, toState, liveProps, vars, carriedMorphs)

		this._buildObjectTweens(child, fromState, toState, liveProps, vars, carriedIds)

		this._continueFills(child, vars)

		this._buildSourceTweens(child, fromState, toState, liveProps, vars)

		// LAST: React's object sync stays gated off until every settle above has landed.
		//
		// ⚠️ PARENT-scoped under a run. `clearPixiTransitionProxies` sweeps `window.pixi_objects_map` GLOBALLY, so a successor child opening in this same tick would have its proxies swept out from under it.
		this._registerSettle(child, ({ handingOver }) => {
			if (handingOver) return
			clearPixiTransitionProxies()
			window.pixi_transition_active = false
		})

		this._openUnpaused()

		this._registerCleanup(child, toState, duration)
	}

	/** A source on fills at both ends blends its bag onto every fill node showing it; a file's rate and volume reach the transport through the playhead override. */
	_buildSourceTweens(child, fromState, toState, liveProps, vars) {
		settleCarriedPaths(child)
		const toUsed = fillSourcesOf(toState.objects)
		const sources = {}
		for (const source of fillSourcesOf(fromState.objects)) {
			if (!toUsed.has(source)) continue
			const from = sourceValues(fromState, source)
			const entries = sourceEntries(source, from, sourceValues(toState, source), liveProps)
			if (entries.length) sources[source] = { from, entries, palette: sourcePalette(source, from, entries) }
		}
		if (!Object.keys(sources).length) return
		const proxy = { t: 0 }
		child._sourceBlend = { proxy, sources }
		const file = Object.keys(sources).find(isFileSource)
		const fileKeys = file ? sources[file].entries.filter(e => FILE_TWEEN_KEYS.includes(e.key)).map(e => e.key) : []
		this._registerSettle(child, () => {
			clearFadeSources()
			if (fileKeys.length) setPlayheadOverride(null)
		})
		child.tl.to(
			proxy,
			{
				...vars,
				t: 1,
				onUpdate: () => {
					for (const source in sources) {
						const values = blendSource(sources[source], proxy.t)
						showFadeSource(source, values)
						for (const s of child._stillFills ?? [])
							if (s.source === source && !s.node.destroyed)
								writeSampleValues(s.node, { ...values, mirror: s.mirror })
						if (source === file && fileKeys.length)
							setPlayheadOverride(Object.fromEntries(fileKeys.map(k => [k, values[k]])))
					}
				},
			},
			0
		)
	}

	/**
	 * Every effect's numeric tween, plus the prompt crossfade, plus the one per-frame dispatch that pushes all of them into `live`.
	 *
	 * Sets `_effectTargets` — the per-effect proxy objects `getActiveState()` reads to snapshot a fade mid-flight — and registers the timeline's FIRST `onUpdate`. The object pass chains its render callback onto that one rather than replacing it.
	 */
	_buildEffectTweens(child, fromState, toState, liveProps, vars, carriedMorphs = EMPTY_MAP) {
		// ONE loop over every effect, `diffusion` included. Before the chop `diffusion` and `filter` had a hand-written block each; more effects arrived with the chop, and a pair of hardcoded blocks would have left every other effect un-animated during a preset fade — silently, since the values still land at the end via the final patch. The symptom is a fade where half the params jump at the finish instead of sweeping.
		const bagTargets = {}
		const morphing = {}
		for (const effect of TWEEN_EFFECTS) {
			// An effect absent from ONE side still tweens: absence means every param at its initial, so the endpoint resolves through the contract rather than the effect being skipped. Only absent-from-both has nothing to animate. Skipping on either side is what made a param jump at the finish instead of sweeping.
			if (!fromState[effect] && !toState[effect]) continue
			const { props, snap } = this.getEffectTweenProps(
				effect,
				fromState[effect],
				toState[effect],
				liveProps,
				carriedMorphs
			)

			// A `morph: true` param is interpolated by the SERVER from both endpoints, so it must never reach GSAP — tweening toward an `{a,b,t,f}` envelope yields NaN, which serializes to null and blanks the param. Pull every one out of the numeric props BY THE CONTRACT FLAG, for every effect: a morphable param landing on any other effect is un-animated (it lands at the end via the final patch), never corrupted.
			for (const key of Object.keys(props)) {
				if (!EFFECT_SCHEMAS[effect]?.properties?.[key]?.morph) continue
				delete props[key]
				delete snap[key]
				;(morphing[effect] ??= []).push(key)
			}

			const keys = Object.keys(props)
			if (!keys.length) continue
			const target = {}
			// Resolved: an absent from-param (or a wholly absent from-effect) is at its initial, and GSAP tweening from `undefined` yields NaN.
			for (const key of keys)
				target[key] = fromState[effect]?.[key] ?? EFFECT_SCHEMAS[effect]?.properties?.[key]?.initial
			const tweenVars = { ...vars, ...props }
			if (Object.keys(snap).length > 0) tweenVars.snap = snap
			child.tl.to(target, tweenVars, 0)
			bagTargets[effect] = target
		}
		child._effectTargets = bagTargets

		// Every morphing param gets a progress proxy 0→1 emitting a `{b, t}` envelope each tick. The SERVER interpolates from what it is already showing to `b` — SLERP across CLIP embeddings for `diffusion.prompt`, the same shape for `ip.asset_id` — so the client only animates `t` and re-sends the whole envelope. The envelope lives on the wire for the fade only; the final `setLiveObject` snaps each param back to its plain destination.
		//
		// One loop, driven by the contract flag: a param becomes morphable by declaring `morph: true` and needs no line here. (Endpoints go on the wire plain — no Compel escaping.)
		child._morphProxies = {}
		// Read once for the whole pass. `loadPreset` sets this BEFORE `createTransition`, so it is already correct here.
		const detached = store.getState().playback.transition_detached
		for (const [effect, keys] of Object.entries(morphing)) {
			for (const key of keys) {
				const qualified = `${effect}.${key}`
				const initial = EFFECT_SCHEMAS[effect]?.properties?.[key]?.initial
				const b = toState[effect]?.[key] ?? initial
				// The outgoing value, for the display projection only. A detached fade left its origin behind, so it publishes none.
				const from = detached ? '' : (fromState[effect]?.[key] ?? initial)
				// `effect`/`key`/`b`/`from` ride on the proxy so `_publishMorph` can emit this envelope from outside the tween's closure — a scrub has to drive the dispatch by hand.
				const proxy = { t: 0, effect, key, b, from }
				child._morphProxies[qualified] = proxy
				// Publish the envelope so a display bridge can derive its mode. Same shape the server consumes on the wire, same vocabulary.
				store.dispatch(setMorph([qualified, morphDisplay(b, 0, from)]))
				child.tl.to(
					proxy,
					{
						...vars,
						// Linear — see `morphEnvelope`'s note on progress vs. value. Stated explicitly, and AFTER the spread, so it survives any future change to the shared ease.
						ease: 'none',
						t: 1,
						onUpdate: () => this._publishMorph(qualified),
					},
					0
				)
			}
		}

		// ⚠️ A morph key this fade does NOT re-arm must lose its envelope, or an interrupted fade's sits in the channel forever and every reader crossfades a value that is not changing.
		//
		// ⚠️ BOTH CARRIERS. A hand-over keeps the display registry deliberately, for the successor to re-arm — but a successor whose endpoints are EQUAL for this param arms nothing, and the orphan then outlives the fade that made it. The overlay reads that registry, so it goes on scrolling the dead fade's endpoints against the live clock: under a zero hold the box shows the preset the run just left.
		for (const effect of TWEEN_EFFECTS) {
			for (const key in EFFECT_SCHEMAS[effect]?.properties ?? {}) {
				if (!EFFECT_SCHEMAS[effect].properties[key].morph) continue
				const qualified = `${effect}.${key}`
				if (child._morphProxies[qualified]) continue
				if (store.getState().playback.morphs?.[qualified]) store.dispatch(clearMorph(qualified))
				if (!isMorphEnvelope(paramChannel.get(qualified))) continue
				paramChannel.set(qualified, toState[effect]?.[key] ?? EFFECT_SCHEMAS[effect].properties[key].initial)
			}
		}

		if (Object.keys(bagTargets).length) {
			child.tl.eventCallback('onUpdate', () => this._publishBags(bagTargets))
		}
	}

	/**
	 * Match a group re-created under a fresh id (regrouped by hand, an SVG re-imported) to its counterpart, so it reads as one group MOVING rather than every child crossing a group boundary.
	 *
	 * ⚠️ Rewrite toward FROM, never TO: `pixi_objects_map` is keyed by Redux id and Redux stays at the FROM preset for the whole morph, so only the FROM id resolves to a live pixiObj.
	 */
	_canonicalizeGroupIds(fromMap, toMap) {
		const key = obj => (obj.parentId ?? 'root') + '\u0000' + [...(obj.children ?? [])].sort().join('\u0001')

		// Only a group whose id is absent from the other side can be a re-creation; a group present in both is already itself.
		const fromGroups = new Map()
		for (const [id, obj] of fromMap) {
			if (obj?.type !== 'group' || toMap.has(id)) continue
			if (!obj.children?.length) continue // an empty group has no identity to match on
			const k = key(obj)
			// Ambiguous: two FROM groups with one signature give no basis to pick between them. Poison the entry rather than guess.
			fromGroups.set(k, fromGroups.has(k) ? null : id)
		}
		if (!fromGroups.size) return

		const rename = new Map()
		for (const [id, obj] of toMap) {
			if (obj?.type !== 'group' || fromMap.has(id)) continue
			if (!obj.children?.length) continue
			const k = key(obj)
			const fromId = fromGroups.get(k)
			if (!fromId) continue
			fromGroups.set(k, null) // one FROM group claims at most one TO group
			rename.set(id, fromId)
		}
		if (!rename.size) return

		// Re-key the group itself, then re-point every reference to it. A group's own `children` ids never changed, but a RENAMED group listed inside another group's `children` must be rewritten too.
		for (const [toId, fromId] of rename) {
			const obj = toMap.get(toId)
			toMap.delete(toId)
			toMap.set(fromId, { ...obj, id: fromId })
		}
		for (const [id, obj] of toMap) {
			const mappedParent = rename.get(obj.parentId)
			const kids = obj.children
			const needsKids = kids?.some(cid => rename.has(cid))
			if (!mappedParent && !needsKids) continue
			toMap.set(id, {
				...obj,
				...(mappedParent ? { parentId: mappedParent } : null),
				...(needsKids ? { children: kids.map(cid => rename.get(cid) ?? cid) } : null),
			})
		}
	}

	/** A same-id type change is two objects: the TO side is re-keyed (synthetic → real id returned) so one fades out and one fades in. */
	_splitRetypedIds(fromMap, toMap) {
		const rename = new Map()
		for (const [id, obj] of toMap) {
			const fromObj = fromMap.get(id)
			if (id === 'root' || !fromObj || !obj?.type || fromObj.type === obj.type) continue
			rename.set(id, `${id}\u0000${obj.type}`)
		}
		if (!rename.size) return rename
		for (const [id, synth] of rename) {
			const obj = toMap.get(id)
			toMap.delete(id)
			toMap.set(synth, { ...obj, id: synth })
		}
		const re = v => rename.get(v) ?? v
		for (const [id, obj] of toMap) {
			const patch = {}
			if (rename.has(obj.parentId)) patch.parentId = re(obj.parentId)
			if (obj.children?.some(c => rename.has(c))) patch.children = obj.children.map(re)
			if (rename.has(obj.mask)) patch.mask = re(obj.mask)
			if (rename.has(obj.maskFor)) patch.maskFor = re(obj.maskFor)
			if (Object.keys(patch).length) toMap.set(id, { ...obj, ...patch })
		}
		return new Map([...rename].map(([id, synth]) => [synth, id]))
	}

	/** Each existence-pinned object as `pinned ?? destination`: pinned props hold, the rest tween. */
	// Every shader the destination shows lands on the vocabulary pins, as a load's `keepVocabPins` puts them.
	_mergeVocabPins(toState) {
		const held = vocabPinValues(store.getState().playback.pins)
		return held && toState?.objects ? produce(toState, d => fillVocabPins(d, held)) : toState
	}

	_mergeObjectPins(toState, fromState, liveProps) {
		const carry = carriedObjects(fromState?.objects, liveProps, toState?.objects)
		if (!carry.length || !toState?.objects) return toState
		return { ...toState, objects: applyCarry({ ...toState.objects }, carry) }
	}

	/**
	 * Every per-object tween: the root background fill, the appear/disappear fades, the visibility and mask crossings, the group-boundary reparent, and the generic per-key transform tween. Also chains the PixiJS render callback onto the timeline (GSAP's ticker is stopped, so nothing draws without it).
	 *
	 * `carriedIds` comes from the kill() above — objects still owned after an interrupt, mid-fade-in, which must not be restarted at alpha 0.
	 */
	_buildObjectTweens(child, fromState, toState, liveProps, vars, carriedIds) {
		if (!fromState.objects || !toState.objects) return
		const fromMap = new Map(Object.entries(fromState.objects))
		const toMap = new Map(Object.entries(toState.objects))

		this._canonicalizeGroupIds(fromMap, toMap)
		const retyped = this._splitRetypedIds(fromMap, toMap)

		// Collect all object IDs from both states
		const allIds = new Set([...fromMap.keys(), ...toMap.keys()])

		this._buildBackgroundFill(child, fromMap, toMap, vars)

		// Carried objects can be absent from BOTH states: created by an interrupted fade-in, and the new preset doesn't have them either. They are on screen at a partial alpha, so they need a fade-OUT — without this they are never visited and would either sit frozen on the stage or be deleted outright (a vanish).
		for (const cid of carriedIds) {
			if (!allIds.has(cid)) {
				allIds.add(cid)
				// Synthesize a fromObj at the object's CURRENT level so the fade-out starts where the eye last saw it.
				const p = this._ownedPixiObj(cid, child)
				if (p)
					fromMap.set(cid, {
						id: cid,
						type: child._owned.get(cid)?.type,
						opacity: p.opacity ?? p.alpha * 100,
					})
			}
		}

		// Every stencil in each state. A stencil is not painted there, so gaining or losing the role is a visibility crossing — `isShownIn` reads these and the ordinary crossing machinery does the rest.
		const stencils = { from: maskMembers(fromMap), to: maskMembers(toMap) }
		const handOff = depthHandOff(fromMap, toMap, stencils, liveProps)
		// The subset whose mask LINK changed: only those need a coverage animation, and only there does a stencil's alpha belong to its target rather than to itself.
		const maskOnlyIn = { from: new Set(), to: new Set() }
		// Ids the loop below resolves as visibility crossings — a mask clone must not follow one, since a crossing pins the live object to the side it is drawn on.
		const crossingIds = new Set()
		for (const id of allIds) {
			if (id === 'root') continue
			const fMask = fromMap.get(id)?.mask || null
			const tMask = toMap.get(id)?.mask || null
			if (fMask === tMask) continue // no crossing — includes masked-identically on both sides, which costs nothing
			if (fMask) addSubtree(maskOnlyIn.from, fMask, fromMap)
			if (tMask) addSubtree(maskOnlyIn.to, tMask, toMap)
		}

		for (const id of allIds) {
			if (id === 'root') continue

			const fromObj = fromMap.get(id)
			const toObj = toMap.get(id)

			// An object owned by this transition (created for a fade-in) is not in pixi_objects_map — React's cleanup would destroy it while Redux is still at the FROM preset — so check ownership too.
			let pixiObj = window.pixi_objects_map?.get(id)?.pixiObj ?? this._ownedPixiObj(id, child)

			if ((!toObj && maskOnlyIn.from.has(id)) || (!fromObj && maskOnlyIn.to.has(id))) continue

			// Object only in fromState — fade out
			if (!toObj) {
				if (!pixiObj) continue
				if (fromObj.type === 'group') continue
				// Alias-aware: PixiShape carries the 0–100 `opacity` alias, a Sprite (image) only native `alpha` (0–1). See setOpacity / setOpacityVars. A carried object is already mid-fade — re-asserting the preset opacity would pop it to full before fading out. Fade from where it actually is.
				if (!carriedIds.has(id)) setOpacity(pixiObj, fromObj.opacity ?? 100)
				child.tl.to(pixiObj, { ...vars, ...setOpacityVars({}, pixiObj, 0) }, 0)
				this._driveChildAcrossMirror({ child, pixiObj, fromObj, toObj: null, fromMap, toMap, vars })
				// Own it for the duration of the fade-out. Whether it survives is decided at release time against the state that actually follows — never by a deferred callback on this timeline, which a further interrupt would discard.
				this._own(child, id, pixiObj, fromObj.type, window.pixi_objects_map?.has(id) ? 'adopted' : 'created')
				continue
			}

			// Object only in toState — create on stage at opacity 0, then fade in
			if (!fromObj) {
				if (!pixiObj && window.pixi_create_object && window.pixi_app) {
					pixiObj = window.pixi_create_object(toObj)
					if (pixiObj) {
						// Ungated so updatePixiObject draws the path
						ungated(() => window.pixi_update_object(pixiObj, toObj))
						pixiObj.alpha = 0
						window.pixi_app.stage.addChild(pixiObj)
						// Depth is fixed by _applyStageOrder once the whole set exists — see below. (addChild appends to the top; React's z-order pass is gated off for the duration of the transition, so nothing else corrects it mid-fade.) A child of a group renders flat on the stage during the transition (never parented into the group Container until React re-syncs on complete), but updatePixiObject set its group-LOCAL x/y/scale. Compose the group's transform in so it fades in at its true world pose instead of near the origin. One level only (resolveWorldTransform); nested groups are a known limitation — see geometry.md. Image size is handled below via the texture load (needs group scale too), so pass it through.
						const worldT =
							toObj.parentId && toObj.parentId !== 'root'
								? resolveWorldTransform(inheritedPose(toObj), toMap)
								: null
						if (worldT) {
							// An image's scale is composed later by _loadFadeInImageTexture (size/texture × group scale), which carries the inherited mirror itself — shifting here too would double-correct.
							if (toObj.type !== 'image') {
								applyInheritedMirror(worldT, toObj, toMap)
								pixiObj.scale.x = worldT.scaleX
								pixiObj.scale.y = worldT.scaleY
								// React reparents into the group Container, which re-applies the group scale; nothing else undoes the composed one.
								if (VECTOR_TYPES.has(toObj.type)) {
									this._settleList(child, '_vectorScaleRestores', () =>
										this._settleVectorScales(child)
									).push({
										id,
										pixiObj,
									})
								}
							}
							pixiObj.x = worldT.x
							pixiObj.y = worldT.y
							pixiObj.rotation = (worldT.rotation * Math.PI) / 180
						}
						this._driveChildAcrossMirror({ child, pixiObj, fromObj: null, toObj, fromMap, toMap, vars })
						// Owned by the transition, deliberately NOT in pixi_objects_map: Redux does not know this object yet, and React destroys any mapped id outside the active tree — mid-fade, while tweens hold the reference.
						this._own(child, id, pixiObj, toObj.type, 'created')
						// Images are created with Texture.EMPTY; React's loadImageTexture runs only after the transition completes, so without this the sprite has no pixels to fade in and pops in at the end. Kick off the load now and size it once the texture arrives (mirrors usePixiObjects sizing). Guard the async apply against kill() by re-checking it's still a fade-in obj. Pass the group world-scale so an image inside a scaled group loads at the correct size (its own scale = size/texture, then × group scale).
						if (toObj.type === 'image') {
							this._loadFadeInImageTexture(child, id, pixiObj, toObj, worldT)
						}
					}
				}
				if (!pixiObj) continue
				// Own it whether we just created it or carried it over from an interrupted fade — either way Redux has no entry yet, so React must not see it until this transition completes. Re-registering a carried object keeps it tracked with no untracked frame in between.
				if (!window.pixi_objects_map?.has(id)) this._own(child, id, pixiObj, toObj.type, 'created')
				if (retyped.has(id)) child._owned.get(id).realId = retyped.get(id)
				this._createFill(child, id, toObj, toMap, toState)
				// Flat on the stage it inherits nothing, so a hidden ancestor must hide it here; its fill follows.
				if (!isShownIn(toObj, toMap, stencils.to)) {
					pixiObj.visible = false
					continue
				}
				const targetOpacity = toObj.opacity ?? getInitialValue(toObj.type, 'opacity') ?? 100
				if (toObj.type === 'group') {
					setOpacity(pixiObj, targetOpacity)
					continue
				}
				// Alias-aware fade-in start/target — see setOpacity / setOpacityVars. A carried-over object is already mid-fade; restarting it at 0 would pop.
				if (!carriedIds.has(id)) setOpacity(pixiObj, 0)
				child.tl.to(pixiObj, { ...vars, ...setOpacityVars({}, pixiObj, targetOpacity) }, 0)
				continue
			}

			if (!pixiObj) continue

			const tweenProps = {}

			// Visibility crossing — treated as existence (see isShown). Hidden⇄shown fades via opacity instead of popping; the discrete `visible` flag is applied at the edge of the morph so the object only renders while it is actually fading.
			//
			// EFFECTIVE visibility, not the object's own flag: a group-boundary crosser can be visible:true on both sides while its DESTINATION parent group is hidden. By its own flag that is no crossing at all, so it never fades — it morphs at full opacity and then blinks out when React reparents it into the hidden group on completion. Walking the parent chain makes that read as the fade-out it is.
			const shownFrom = isShownIn(fromObj, fromMap, stencils.from)
			// A pinned `visible` governs the object's OWN flag only; `isShownIn` still walks the parent chain, so a pinned-visible object under a hidden group is still hidden.
			const effTo = isPinned(liveProps, `objects.${id}.visible`) ? { ...toObj, visible: isShown(fromObj) } : toObj
			const shownTo = isShownIn(effTo, toMap, stencils.to)
			// A crossing owns GEOMETRY as well as the fade: the hidden side's pose was never rendered, so interpolating against it animates toward a place no eye has seen. The visible side's pose is pinned for the whole fade.
			const crossing = shownFrom !== shownTo
			// ⚠️ Losing the STENCIL role is not disappearing: the object is placed on both sides and its clone already draws that pose, so pinning strands it. Only the drawn role crosses.
			const stencilRoleOnly =
				crossing &&
				isShownIn(fromObj, fromMap, null) === isShownIn(effTo, toMap, null) &&
				stencils.from.has(id) !== stencils.to.has(id)
			if (crossing) crossingIds.add(id)
			const fillSwapped = fillSourceOf(fromObj) !== fillSourceOf(toObj)
			// What an interrupted fade left of the shape's own colour under a source coming in.
			const carriedColour = isSourceFill(fromObj.fill) ? child._fillCarry?.get(id)?.colour : undefined
			if (crossing) {
				// Land the visible side's instant-only props (flip, zoom/pan, blend, font, path data …) before the fade. Driven through updatePixiObject rather than reimplemented per type; the flag is lowered so its transition guards do not suppress the write.
				ungated(() => window.pixi_update_object?.(pixiObj, shownTo ? toObj : fromObj))
				// Fading in, the shape shows its TO fill from frame one: the hidden side has no picture to dissolve from.
				if (shownTo && fillSwapped) {
					const carried = this._takeFillCarry(child, id)
					if (carried) for (const o of carried.olds) dropOld(o)
					const entry = window.pixi_objects_map?.get(id)
					const old =
						entry &&
						window.pixi_swap_fill?.(id, entry, toObj, { objects: toMap, bags: toState.source_settings })
					if (old) window.pixi_drop_fill_node?.(old)
				}
				// Fade level of the visible side (null opacity → schema initial; see the opacity:null data issue on old presets).
				const shownObj = shownTo ? toObj : fromObj
				// The pinned pose is group-LOCAL, so when the two sides sit under different parents the object renders wherever the OTHER side's parent happens to be. Hold the visible side's world pose on the stage instead.
				if (!stencilRoleOnly && fromObj.parentId !== toObj.parentId && window.pixi_app?.stage) {
					const w = resolveWorldTransform(inheritedPose(shownObj), shownTo ? toMap : fromMap)
					window.pixi_app.stage.addChild(pixiObj)
					pixiObj.x = w.x
					pixiObj.y = w.y
					pixiObj.scale.x = w.scaleX
					pixiObj.scale.y = w.scaleY
					pixiObj.rotation = (w.rotation * Math.PI) / 180
					if (VECTOR_TYPES.has(shownObj.type))
						this._settleList(child, '_vectorScaleRestores', () => this._settleVectorScales(child)).push({
							id,
							pixiObj,
						})
				}
				const shownOpacity = shownObj.opacity ?? getInitialValue(shownObj.type, 'opacity') ?? 100
				// Render for the duration — a hidden object is only free when hidden on BOTH sides.
				pixiObj.visible = true
				setOpacity(pixiObj, shownTo ? 0 : shownOpacity)
				const targetOpacity = shownTo ? shownOpacity : 0
				child.tl.to(pixiObj, { ...vars, ...setOpacityVars({}, pixiObj, targetOpacity) }, 0)
				// Settle the discrete flag when the fade lands, and restore the fade level so a later re-show doesn't start from 0. Registered rather than inlined as a timeline callback so kill() settles it too — see _settleVisibilityCrossings.
				if (!shownTo) {
					// Settle to the object's OWN destination flag, not to false. When the object is invisible only because its destination parent group is hidden, its own flag stays true — forcing false here would corrupt it for any later preset that shows that group.
					this._settleList(child, '_visibilityCrossings', ({ successor }) =>
						this._settleVisibilityCrossings(child, successor)
					).push({ id, pixiObj, visible: isShown(effTo), opacity: shownOpacity })
				}
			}

			// Check if group structure changed
			const groupChanged =
				fromObj.parentId !== toObj.parentId ||
				fromObj.children?.length !== toObj.children?.length ||
				fromObj.children?.some((cid, i) => toObj.children[i] !== cid)

			// Object type + schema props (used by the reparent guard and the tween loop below)
			const objType = toObj.type ?? fromObj.type
			const objProps = display_object_schema.definitions[objType]?.properties

			// `groupChanged` also fires on a mere `children` delta. A group whose own parent is unchanged keeps its world frame, so the instant-apply below would snap it — dragging every child by the group's delta on frame one.
			const framePreserved = objType === 'group' && fromObj.parentId === toObj.parentId

			// A child crossing the group boundary (group ⇄ ungrouped). Redux `objects` stays at the FROM state for the whole morph, so `syncChildren` never reparents this pixiObj — it physically stays in the FROM container, and if that container is a group its alpha tween (fade in/out) would drag the child's rendered alpha toward 0. Its from/to x/y also live in different frames (group-local vs. world). Fix: reparent the pixiObj to the stage NOW (so it lives in world space and escapes the fading group), then TWEEN its transform from its FROM world pose to its TO world pose. When the ungroup is pure bookkeeping the two poses coincide → the tween holds it still; when the preset also moves/scales/rotates the (un)grouped child, the delta animates smoothly. We own the transform props for this child (skip both the instant-apply and generic tween below).
			let reparented = false
			let frozenPose = null
			// A crosser's mirror is its WORLD sign: one inherited from a group flips it as surely as its own.
			let crossFlipped = null

			if (objType !== 'group')
				this._driveChildAcrossMirror({ child, pixiObj, fromObj, toObj, fromMap, toMap, vars })

			if (fromObj.parentId !== toObj.parentId && objType !== 'group' && window.pixi_app?.stage) {
				crossFlipped = mirrorFlipped(
					resolveWorldTransform(fromObj, fromMap),
					resolveWorldTransform(toObj, toMap)
				)
				const fromW0 = resolveWorldTransform(inheritedPose(fromObj), fromMap)
				const toW = resolveWorldTransform(inheritedPose(toObj), toMap)

				// A mirror is discrete: both endpoints take the DESTINATION sign, and only the magnitude sweeps — the policy the image branch below already applies. Lerping a signed scale passes through 0 at the midpoint, collapsing the object to a sliver before it re-expands mirrored.
				//
				// Re-anchoring x/y is the other half, and it is not optional once the sign is pinned. `x` is the ORIGIN, and under a negative scale a geometry-owning type renders on the other side of it (`signedExtent`, objectBounds.js) — so the same rendered position is described by a DIFFERENT origin at each sign. Tweening the raw origins therefore traverses that difference as a translation: the object slides the full width of its own frame shift and lands correctly, which is the jump. Re-expressing the FROM origin under the destination sign puts both endpoints in one frame, leaving only the genuine motion.
				const scaleRealized = SCALE_REALIZED_TYPES.has(objType)
				// A vector's inherited mirror lives on the group Container and is re-applied there once React reparents, so composing it onto the object too renders it mirrored twice for the whole fade. Strip the sign and move the origin to the mirrored box's near edge instead, which is what the object will render at once reparented.
				if (!scaleRealized) applyInheritedMirror(toW, toObj, toMap)
				// A scale-realized mirror turns about the pivot (the sprite anchor), so its sign needs no pinning and each end keeps its own. Only a geometry-owning type must hold one sign for the whole tween.
				if (scaleRealized) {
					// ⚠️ The sprite renders through ONE texture for the whole fade, so both endpoints size against it.
					const tex = this._pendingTextures?.get(toObj.source) ?? pixiObj.texture
					const tw = tex?.width || 0
					const th = tex?.height || 0
					if (tw > 1 && th > 1) {
						fromW0.scaleX *= (fromObj.width ?? tw) / tw
						fromW0.scaleY *= (fromObj.height ?? th) / th
						toW.scaleX *= (toObj.width ?? tw) / tw
						toW.scaleY *= (toObj.height ?? th) / th
					}
				}
				const fromW = scaleRealized
					? { ...fromW0 }
					: {
							...fromW0,
							scaleX: Math.sign(toW.scaleX) * Math.abs(fromW0.scaleX),
							scaleY: Math.sign(toW.scaleY) * Math.abs(fromW0.scaleY),
						}
				// A vector's own sign lands on its draw scale at frame one (the per-key loop below), so its box turns when the WORLD sign does: inherited × own. An inherited flip cancelled by an own one turns nothing.
				const ownFlips = key =>
					VECTOR_TYPES.has(objType) &&
					objType !== 'brush' &&
					!crossing &&
					!isPinned(liveProps, `objects.${id}.${key}`) &&
					signOf(fromObj[key]) !== signOf(toObj[key])
				const turnsX =
					!scaleRealized && (Math.sign(fromW0.scaleX) !== Math.sign(toW.scaleX)) !== ownFlips('scaleX')
				const turnsY =
					!scaleRealized && (Math.sign(fromW0.scaleY) !== Math.sign(toW.scaleY)) !== ownFlips('scaleY')

				// The mirror turns the box about the pivot, `(near + far) − 2·pivot` along that axis. ⚠️ `getLocalBounds()`, never `data`: an interrupt pairs the live pose with the destination's `data`.
				if (turnsX || turnsY) {
					const lb = pixiObj.getLocalBounds?.() ?? localExtent(fromObj)
					const px = pixiObj.pivot?.x ?? 0
					const py = pixiObj.pivot?.y ?? 0
					const r = (fromW.rotation * Math.PI) / 180
					const cos = Math.cos(r)
					const sin = Math.sin(r)
					if (turnsX) {
						const d = (2 * lb.x + lb.width - 2 * px) * fromW0.scaleX
						fromW.x += d * cos
						fromW.y += d * sin
					}
					if (turnsY) {
						const d = (2 * lb.y + lb.height - 2 * py) * fromW0.scaleY
						fromW.x -= d * sin
						fromW.y += d * cos
					}
				}

				// A crosser that is ALSO a visibility crossing still reparents — it must escape a fading group — but both endpoints collapse onto the visible side's world pose, so it holds still while only opacity moves.
				if (crossing && !stencilRoleOnly) {
					const held = shownTo ? toW : fromW
					fromW.x = toW.x = held.x
					fromW.y = toW.y = held.y
					fromW.scaleX = toW.scaleX = held.scaleX
					fromW.scaleY = toW.scaleY = held.scaleY
					fromW.rotation = toW.rotation = held.rotation
				}

				frozenPose = {
					parent: pixiObj.parent,
					x: pixiObj.x,
					y: pixiObj.y,
					scaleX: pixiObj.scale.x,
					scaleY: pixiObj.scale.y,
					rotation: pixiObj.rotation,
				}
				window.pixi_app.stage.addChild(pixiObj)
				pixiObj.x = fromW.x
				pixiObj.y = fromW.y
				pixiObj.scale.x = fromW.scaleX
				pixiObj.scale.y = fromW.scaleY
				pixiObj.rotation = (fromW.rotation * Math.PI) / 180
				reparented = true

				// See _settleVectorScales.
				if (VECTOR_TYPES.has(objType)) {
					this._settleList(child, '_vectorScaleRestores', () => this._settleVectorScales(child)).push({
						id,
						pixiObj,
					})
				}

				const toRot = shortestRotation(fromW.rotation, toW.rotation)

				// ⚠️ Only the MAGNITUDE sweeps: a signed lerp across a mirror passes through 0 and collapses the object to a sliver at the midpoint.
				const mSignX = Math.sign(toW.scaleX) || 1
				const mSignY = Math.sign(toW.scaleY) || 1
				const fromMagX = mSignX * Math.abs(fromW.scaleX)
				const toMagX = mSignX * Math.abs(toW.scaleX)
				const fromMagY = mSignY * Math.abs(fromW.scaleY)
				const toMagY = mSignY * Math.abs(toW.scaleY)
				pixiObj.x = fromW.x
				pixiObj.y = fromW.y
				pixiObj.scale.x = fromMagX
				pixiObj.scale.y = fromMagY

				const changed =
					fromW.x !== toW.x ||
					fromW.y !== toW.y ||
					fromMagX !== toMagX ||
					fromMagY !== toMagY ||
					fromW.rotation !== toRot
				if (changed) {
					// getActiveState reads the live pixiObj transform each frame, so no proxy state is needed for interruption coherence — the onUpdate keeps the object authoritative.
					const proxy = { t: 0 }
					child.tl.to(
						proxy,
						{
							...vars,
							t: 1,
							onUpdate: () => {
								const p = proxy.t
								pixiObj.x = fromW.x + (toW.x - fromW.x) * p
								pixiObj.y = fromW.y + (toW.y - fromW.y) * p
								pixiObj.scale.x = fromMagX + (toMagX - fromMagX) * p
								pixiObj.scale.y = fromMagY + (toMagY - fromMagY) * p
								pixiObj.rotation = ((fromW.rotation + (toRot - fromW.rotation) * p) * Math.PI) / 180
							},
						},
						0
					)
				}
			}

			// ⚠️ BEFORE the mirror branch below writes the destination sign — the freeze must see the FROM side. Groups excluded: their texture contains their children, drawing each twice.
			const folds = !crossing && !reparented && !pixiObj._mirrorPositionOwned
			const foldX = folds && foldsMirror(id, fromObj, toObj, 'scaleX', liveProps)
			const foldY = folds && foldsMirror(id, fromObj, toObj, 'scaleY', liveProps)
			const flipped =
				crossFlipped ??
				((signOf(fromObj.scaleX) !== signOf(toObj.scaleX) && !foldX) ||
					(signOf(fromObj.scaleY) !== signOf(toObj.scaleY) && !foldY))
			const sourceSwapped = objType === 'image' && !!toObj.source && fromObj.source !== toObj.source
			const fillMirrored = !fillSwapped && sourceMirrorFlipped(fromObj, toObj, fromState, toState, liveProps)
			const pathed =
				!crossing &&
				shownFrom &&
				!fillMirrored &&
				this._pathFill(child, id, fromObj, toObj, fromState, toState, liveProps, vars)
			if (!pathed && !crossing && shownFrom && (fillSwapped || fillMirrored))
				this._crossfadeFill(child, id, toObj, toMap, toState, vars, fillMirrored)
			else if (!crossing && shownFrom) this._morphFillField(child, id, fromObj, toObj, toMap, toState, vars)
			const rises = handOff.rises.has(id)
			// ⚠️ `!crossing` alone also admits the object hidden on BOTH sides, which has no picture to freeze: its bounds are 0×0, so the still is scaled to a zero matrix and `_syncGhosts` declines to repair it for the whole fade.
			if (
				!crossing &&
				shownFrom &&
				objType !== 'group' &&
				(flipped || sourceSwapped || (depthMoved(id, fromMap, toMap) && !handOff.live.has(id)))
			) {
				const live = frozenPose && {
					parent: pixiObj.parent,
					x: pixiObj.x,
					y: pixiObj.y,
					scaleX: pixiObj.scale.x,
					scaleY: pixiObj.scale.y,
					rotation: pixiObj.rotation,
				}
				if (frozenPose) {
					frozenPose.parent?.addChild(pixiObj)
					pixiObj.x = frozenPose.x
					pixiObj.y = frozenPose.y
					pixiObj.scale.x = frozenPose.scaleX
					pixiObj.scale.y = frozenPose.scaleY
					pixiObj.rotation = frozenPose.rotation
				}
				// Two copies of ONE picture need no dissolve, and fading both dips coverage. They stop being one picture once the subject's own geometry or mask changes too.
				const pictureChanges = fromObj.data !== toObj.data || (fromObj.mask ?? null) !== (toObj.mask ?? null)
				// An opaque object rising over its still covers it, so the still holds rather than dipping coverage.
				const opaque = (fromObj.opacity ?? 100) === 100 && (toObj.opacity ?? 100) === 100
				this._crossfadeFrom(
					child,
					id,
					pixiObj,
					vars,
					toObj.opacity ?? 100,
					flipped || sourceSwapped || pictureChanges || rises,
					!(rises && opaque)
				)
				if (sourceSwapped) this._swapToDestinationTexture(child, id, pixiObj, toObj, fromObj)
				if (live) {
					window.pixi_app.stage.addChild(pixiObj)
					pixiObj.x = live.x
					pixiObj.y = live.y
					pixiObj.scale.x = live.scaleX
					pixiObj.scale.y = live.scaleY
					pixiObj.rotation = live.rotation
				}
			}

			// Scale-realized size tween (image, group): no `data` to morph, size realized by pixiObj.scale rather than by width/height (tween:false) or the phantom `scaleX` on a plain Container. The operator rides width/height; scale.x/y is derived each frame, mirroring usePixiObjects.
			//
			// Skipped for a frame-changing group (that path applies scale instantly) and for a crossing, whose endpoints would come from the hidden side.
			if (!crossing && (!groupChanged || framePreserved) && SCALE_REALIZED_TYPES.has(objType)) {
				if (objType === 'image') {
					const texW = pixiObj.texture?.width
					const texH = pixiObj.texture?.height
					// Only the MAGNITUDE sweeps, and it is `width`/`height`. `scaleX` on an image carries the mirror alone, so both endpoints take the DESTINATION sign — the flip reads on frame one and the size tweens under it, rather than squashing through zero at the midpoint.
					const signX = signOf(toObj.scaleX)
					const signY = signOf(toObj.scaleY)
					const fromSX = signX * fromObj.width,
						toSX = signX * toObj.width
					const fromSY = signY * fromObj.height,
						toSY = signY * toObj.height
					if (texW > 1 && texH > 1) {
						pixiObj.scale.x = signX * Math.abs(pixiObj.scale.x)
						pixiObj.scale.y = signY * Math.abs(pixiObj.scale.y)
					}
					// The sprite mirrors in place about its anchor, so x/y tween as plain keys below.
					// Skip if texture not yet loaded (dims unknown) or the size is unchanged.
					if (texW > 1 && texH > 1 && (fromSX !== toSX || fromSY !== toSY)) {
						const proxy = { t: 0 }
						pixiObj._scaleTransitionProxy = proxy
						child.tl.to(
							proxy,
							{
								...vars,
								t: 1,
								onUpdate: () => {
									const tw = pixiObj.texture?.width || texW
									const th = pixiObj.texture?.height || texH
									pixiObj.scale.x = (fromSX + (toSX - fromSX) * proxy.t) / tw
									pixiObj.scale.y = (fromSY + (toSY - fromSY) * proxy.t) / th
								},
							},
							0
						)
					}
				} else {
					// group — renders straight from scale.x/y; a width edit already stored scaleX = width/childBbox, so tweening scaleX IS tweening the ridden width.
					// Only the MAGNITUDE sweeps: both endpoints take the DESTINATION sign, exactly as the image branch above. Lerping a signed scale across a mirror passes through 0 at the midpoint, collapsing the group to a zero-width sliver before it re-expands mirrored.
					const signX = signOf(toObj.scaleX)
					const signY = signOf(toObj.scaleY)
					const fromSX = signX * Math.abs(fromObj.scaleX ?? 1),
						toSX = signX * Math.abs(toObj.scaleX ?? 1)
					const fromSY = signY * Math.abs(fromObj.scaleY ?? 1),
						toSY = signY * Math.abs(toObj.scaleY ?? 1)

					// The mirror lands NOW, before the change test — a pure flip has equal magnitudes on both sides, so it would otherwise be skipped entirely and only settle at the end.
					pixiObj.scale.x = fromSX
					pixiObj.scale.y = fromSY

					if (fromSX !== toSX || fromSY !== toSY) {
						const proxy = { t: 0 }
						pixiObj._scaleTransitionProxy = proxy
						child.tl.to(
							proxy,
							{
								...vars,
								t: 1,
								onUpdate: () => {
									pixiObj.scale.x = fromSX + (toSX - fromSX) * proxy.t
									pixiObj.scale.y = fromSY + (toSY - fromSY) * proxy.t
								},
							},
							0
						)
					}
				}
			}

			const allKeys = new Set([...Object.keys(fromObj), ...Object.keys(toObj)])
			for (const key of allKeys) {
				if (VECTOR_TYPES.has(objType) && (key === 'scaleX' || key === 'scaleY')) {
					if (crossing || objType === 'brush' || isPinned(liveProps, `objects.${id}.${key}`)) continue
					const from = fromObj[key] ?? 1
					const to = toObj[key] ?? 1
					const isX = key === 'scaleX'
					if ((isX ? foldX : foldY) && pixiObj._rawPath)
						pixiObj.rawPath = mirrorPath(pixiObj._rawPath, pixiObj._unscaledPivot, { x: isX, y: !isX })
					pixiObj[key] = signOf(to) * Math.abs(from)
					if (Math.abs(from) !== Math.abs(to)) tweenProps[key] = to
					continue
				}

				// Skip a key unless the engine has a way to transition it: either it is tweenable (continuous numeric, generic path below) or it is one of the bespoke-strategy keys handled explicitly (data/fill/stroke). This replaces the old IGNORE_PROPS deny-list — structural/discrete/tween:false props are non-tweenable via the shared classifier.
				if (!classifyParam(objProps?.[key]).tweenable && !SPECIAL_TWEEN_KEYS.has(key)) continue
				if (isPinned(liveProps, `objects.${id}.${key}`)) continue

				// ⚠️ NO scale-realized type reaches the generic path. Only `PixiShape` (rect/circle/path) has a `scaleX`/`scaleY` alias; a group is a Container and an image is a Sprite, so GSAP finds no such property and throws "Invalid property scaleX ... Missing plugin?". A group's sweep is the scale-proxy above (or the instant-apply below when its frame changes); an image does not sweep at all — its magnitude rides width/height and its scale carries only the mirror sign, applied instantly.
				if (SCALE_REALIZED_TYPES.has(objType) && (key === 'scaleX' || key === 'scaleY')) {
					if (!(objType === 'group' && groupChanged && !framePreserved)) continue
				}

				// A present-but-null value means "unset", exactly like an absent key — old presets persist explicit nulls (`opacity`, `flipX`, `locked`, `mask`). `key in obj` alone would skip the schema fallback and hand the raw null to the tween: `setOpacityVars` then computes `alpha = null / 100 = 0`, fading a fully-visible object to black for the whole morph before `updatePixiObject`'s own `?? 100` snaps it back on complete.
				const toValue = toObj[key] ?? getInitialValue(toObj.type ?? fromObj.type, key)
				const fromValue = fromObj[key] ?? getInitialValue(fromObj.type, key)
				if (toValue === undefined || fromValue === undefined) continue

				if (groupChanged && !framePreserved && TRANSFORM_PROPS.has(key)) {
					// Reparented group-boundary crossers have their transform driven by the dedicated world-space tween registered above (from FROM-world to TO-world pose) — skip the per-key transform handling so it doesn't fight that tween.
					if (reparented) continue
					// Apply instantly — bypass PixiShape alias, set native PixiJS props
					if (key === 'rotation') {
						pixiObj.rotation = toValue * (Math.PI / 180)
					} else if (key === 'scaleX') {
						pixiObj.scale.x = toValue
					} else if (key === 'scaleY') {
						pixiObj.scale.y = toValue
					} else {
						pixiObj[key] = toValue
					}
					continue
				}

				if (pixiObj._mirrorPositionOwned && (key === 'x' || key === 'y')) continue

				// Realized on Pixi's own pivot/anchor, tweened together with the geometry below.
				if (key === 'pivotX' || key === 'pivotY') continue

				// The crossing pinned the visible side's pose; a shape or transform key here would drag it toward the hidden side's. `opacity` is exempt — the crossing's own fade is registered as an opacity tween.
				if (crossing && (TRANSFORM_PROPS.has(key) || CROSSING_SHAPE_PROPS.has(key))) continue

				if (key !== 'data' && fromValue === toValue && !(key === 'fill' && carriedColour)) continue

				if (key === 'data') {
					// Variable-width brush: morph centerline, rebuild outline each frame
					if (fromObj.variableWidth && fromObj.centerline && toObj.centerline) {
						const endCl = stringToRawPath(toObj.centerline)
						tweenProps.morph = endCl
						pixiObj._rawPath = stringToRawPath(fromObj.centerline)
						pixiObj._brushMorphParams = {
							strokeWidth: (toObj.minWidth || 0) + (toObj.widthGrowth ?? toObj.maxWidth ?? 8),
							strokeCap: toObj.strokeCap ?? 'round',
							minWidth: toObj.minWidth ?? 0,
							widthProfile: toObj.widthProfile,
						}
					} else {
						// Normal path morph via MorphPlugin
						const endRaw = stringToRawPath(toValue)
						tweenProps.morph = endRaw
						// Ensure pixiObj has a starting rawPath initialized
						if (!pixiObj._rawPath) {
							pixiObj._rawPath = stringToRawPath(fromObj.data)
						}
					}
				} else if (key === 'rotation') {
					tweenProps.rotation = shortestRotation(fromValue, toValue) * (Math.PI / 180)
				} else if (key === 'fill') {
					// Manual lerp rather than GSAP's colour tween — GSAP emits `rgba()`, which breaks parseHexAlpha. A null pair (linear↔radial, or a source at both ends) lands at the settle.
					const pair = shapeFillPair(carriedColour ?? fromValue, toValue)
					if (pair) {
						const proxy = { t: 0, pair }
						pixiObj._fillTransitionProxy = proxy
						child.tl.to(
							proxy,
							{
								...vars,
								t: 1,
								onUpdate: () => {
									const fill = pair.at(proxy.t)
									pixiObj.setFillDirect(pair.solid ? parseHexAlpha(fill) : toPixiFill(fill))
								},
							},
							0
						)
					}
				} else if (key === 'stroke') {
					// Map to strokeColor alias (avoids shadowing Graphics.stroke() method)
					tweenProps.strokeColor = toValue
				} else if (key === 'opacity') {
					// A visibility crossing already owns the fade (and wins over any opacity target — fading to hidden means fading to 0, whatever `opacity` says).
					if (crossing) continue
					// Alias-aware: an `image` is a plain Sprite with no `opacity` alias, so it tweens native `alpha` (the fade in/out branches above do the same).
					setOpacityVars(tweenProps, pixiObj, toValue)
				} else if (key === 'visible') {
					// Discrete flag — owned by the visibility-crossing block above, never tweened.
					continue
				} else {
					// x, y, scaleX (PixiShape alias), scaleY (PixiShape alias), etc.
					tweenProps[key] = toValue
				}
			}

			if (Object.keys(tweenProps).length) {
				// Rotation is about the pivot, which `x`/`y` name, so position and rotation tween independently: no arc to solve for.
				child.tl.to(pixiObj, { ...vars, ...tweenProps }, 0)
			}
			this._tweenPivot(child, pixiObj, objType, fromObj, toObj, vars, crossing)
		}

		// Every fade-in object now exists — order the whole stage to the destination in one pass, so objects fade in AT their final depth instead of snapping on completion.
		this._applyStageOrder(child, toMap, stencils.to, fromMap, stencils.from)

		this._placeGhosts(child, fromMap)

		this._buildMaskCrossings(child, allIds, fromMap, toMap, vars, crossingIds)

		this._followFills(child, true)

		const inpaintEntries = this._collectInpaintEntries(allIds, fromMap, toMap)

		// ⚠️ NOTHING RENDERS HERE. The pipeline ticks GSAP — firing this — then renders the stage itself, so a render here draws the frame twice.
		const existingUpdate = child.tl.eventCallback('onUpdate')
		const steps = [
			['effects', existingUpdate],
			// A mask path may itself be animating, and the stencil clone must track it or the coverage drifts off the drawing.
			['masks', () => this._syncMaskClones(child)],
			['mirrors', () => child._mirrorPositions?.forEach(e => e.place())],
			// After every tween rendered: a sample must pair the path with this frame's progress.
			['fieldMorphs', () => child._fillMorphs?.forEach(drive => drive())],
			['fills', () => this._followFills(child)],
			['ghosts', () => this._syncGhosts(child)],
			inpaintEntries.length && [
				'inpaint',
				() =>
					sendInpaintMaskUpdates(
						inpaintEntries.map(({ id, pixiObj, baseObj }) => ({
							...baseObj,
							id,
							x: pixiObj.x,
							y: pixiObj.y,
							rotation: pixiObj.rotation * (180 / Math.PI),
						}))
					),
			],
		].filter(s => s?.[1])
		const failed = new Set()
		child.tl.eventCallback('onUpdate', () => {
			for (const [name, fn] of steps) runStep(failed, name, fn)
		})
	}

	/** Mask crossings — every object whose `mask` LINK differs between the two states. Must run AFTER the fade-in loop (a mask path existing only in toState has to be created before it can be cloned) and after `_applyStageOrder` (the temp container is not an ordered stage participant). Detection keys on the link delta, never on the path's presence: a path can exist on both sides and be linked on only one. An object present on ONE side reads `mask: null` on the other — absence, not "unmasked" — so it gets its mask held for the morph rather than a coverage animation. */
	_buildMaskCrossings(child, allIds, fromMap, toMap, vars, crossingIds = null) {
		// Mask crossings. Built AFTER the fade-in loop so a mask path that only exists in toState has been created and can be cloned; and after _applyStageOrder, since the container is not an ordered stage participant.
		for (const id of allIds) {
			if (id === 'root') continue
			const fMask = fromMap.get(id)?.mask || null
			const tMask = toMap.get(id)?.mask || null
			if (fMask === tMask) continue

			if (!fromMap.has(id) || !toMap.has(id)) {
				const sideMask = toMap.has(id) ? tMask : fMask
				const obj = window.pixi_objects_map?.get(id)?.pixiObj ?? this._ownedPixiObj(id, child)
				if (sideMask && obj && !obj.destroyed && !obj.mask && !obj._eraserMaskGfx) {
					const held = this._buildMaskContainer(child, sideMask, toMap.has(id) ? toMap : fromMap)
					if (held) {
						held.rect.alpha = 0
						obj.setMask({ mask: held.container, inverse: false })
						obj._maskInverse = false
						this._settleList(child, '_maskCrossings', args => this._settleMaskCrossings(child, args)).push({
							id,
							pixiObj: obj,
							...held,
							masking: true,
							toMaskId: toMap.get(id)?.mask || null,
							maskInverse: !!toMap.get(id)?.maskInverse,
						})
					}
				}
				continue
			}

			const pixiObj = window.pixi_objects_map?.get(id)?.pixiObj ?? this._ownedPixiObj(id, child)
			// An eraser mask owns `pixiObj.mask` through the same property (usePixiObjects keeps it on `_eraserMaskGfx`); do not fight it.
			if (!pixiObj || pixiObj.destroyed || pixiObj._eraserMaskGfx) continue

			// RELINK — masked by a DIFFERENT path on each side. Neither endpoint is "unmasked", so coverage has nothing to interpolate along: the rect axis only spans path⇄everything. Handle it by moving the boundary instead of fading it.
			if (fMask && tMask) {
				const relink = this._buildMaskRelink(child, fMask, tMask, fromMap, toMap, vars)
				if (!relink) continue
				pixiObj.setMask({ mask: relink.container, inverse: false })
				pixiObj._maskInverse = false
				this._settleList(child, '_maskCrossings', args => this._settleMaskCrossings(child, args)).push({
					id,
					pixiObj,
					...relink,
					toMaskId: tMask,
					maskInverse: !!toMap.get(id)?.maskInverse,
				})
				continue
			}

			// Clone from the side the mask actually exists on — for an unmask that is fromState, whose objects React still has on screen.
			const maskId = tMask || fMask
			const built = this._buildMaskContainer(child, maskId, tMask ? toMap : fromMap, crossingIds)
			if (!built) continue
			const { container, rect, clones } = built

			// A one-sided stencil has no life as a drawing — its clone does the visual work — so release the AlphaMask flags only when it survives the crossing.
			const stencilSurvives = fromMap.has(maskId) && toMap.has(maskId)
			if (stencilSurvives) clearMask(pixiObj)
			else pixiObj.mask = null
			pixiObj.setMask({ mask: container, inverse: false })
			pixiObj._maskInverse = false
			// ⚠️ A depth ghost is a STILL of the full geometry, frozen before any mask existed; unmasked it paints what the stencil cuts, over the original.
			for (const g of child._ghostCrossfades ?? []) {
				if (g.id !== id || g.ghost.destroyed) continue
				g.ghost.setMask?.({ mask: container, inverse: false })
				g.ghost._maskInverse = false
			}

			// Masking (no link → link): coverage starts covering the stage and closes onto the stencil. Unmasking: the reverse. Either way the mask is installed for the WHOLE morph and only its coverage animates, so nothing pops at the edges.
			const masking = !fMask && !!tMask
			const morphed = this._buildCoverageMorph(child, rect, clones, masking, vars)

			// PixiJS drops a mask from the render build outright, so the stencil's own visibility is discrete: it would blink out (masking) or in (unmasking) on frame one. Keep it in the build and cross-fade its role instead.
			if (stencilSurvives) {
				const stencil = window.pixi_objects_map?.get(maskId)?.pixiObj ?? this._ownedPixiObj(maskId, child)
				if (stencil && !stencil.destroyed) {
					stencil.includeInBuild = true
					stencil.renderable = true
					stencil.measurable = true
					const side = masking ? fromMap.get(maskId) : toMap.get(maskId)
					const full = side.opacity ?? getInitialValue(side.type, 'opacity') ?? 100
					setOpacity(stencil, masking ? full : 0)
					child.tl.to(stencil, { ...vars, ...setOpacityVars({}, stencil, masking ? 0 : full) }, 0)
					this._settleList(child, '_maskStencilFades', () => this._settleMaskStencilFades(child)).push({
						stencil,
						opacity: full,
					})
				}
			} else if (!morphed) {
				// One-sided with no morphable geometry: the clones are the only thing drawing the stencil, and the coverage rect does not touch them. Left at 1 they pop at the settle instead of fading.
				for (const { clone } of clones) {
					if (clone.destroyed) continue
					clone.alpha = masking ? 0 : 1
					child.tl.to(clone, { ...vars, alpha: masking ? 1 : 0 }, 0)
				}
			}

			this._settleList(child, '_maskCrossings', args => this._settleMaskCrossings(child, args)).push({
				id,
				pixiObj,
				container,
				rect,
				clones,
				masking,
				toMaskId: tMask,
				maskInverse: !!toMap.get(id)?.maskInverse,
			})
		}
	}

	/** The inpaint-mask objects whose pose is pushed to the server each tick. Collected once up front so the per-frame callback does no map lookups. */
	_collectInpaintEntries(allIds, fromMap, toMap) {
		const inpaintEntries = []
		for (const id of allIds) {
			if (id === 'root') continue
			const fromObj = fromMap.get(id)
			const toObj = toMap.get(id)
			if (!(fromObj?.inpaint_mask || toObj?.inpaint_mask)) continue
			const entry = window.pixi_objects_map?.get(id)
			if (entry?.pixiObj) inpaintEntries.push({ id, pixiObj: entry.pixiObj, baseObj: toObj ?? fromObj })
		}
		return inpaintEntries
	}

	/** Root background fill. Solid→solid lerps hex; same-type gradients interpolate stops; linear↔radial swaps instantly (no meaningful in-between). Sets `_bgFillProxy`, which `getActiveState()` reads to snapshot a fade mid-flight. */
	_buildBackgroundFill(child, fromMap, toMap, vars) {
		// Animate root background fill — missing root/fill treated as black
		const fromFill = fromMap.get('root')?.fill
		const toFill = toMap.get('root')?.fill
		if (fromFill === toFill || (!fromFill && !toFill)) return
		const bg = window.pixi_bg_ref?.current
		const app = window.pixi_app
		if (!bg || !app) return
		const w = app.screen.width,
			h = app.screen.height

		// `drawBgFill` takes a hex string or a fill object alike, so the pair's shape needs no branch here.
		const pair = prepareFillPair(fromFill, toFill)
		if (!pair) {
			drawBgFill(bg, toFill, w, h)
			return
		}
		const proxy = { t: 0 }
		child._bgFillProxy = proxy
		child.tl.to(proxy, { ...vars, t: 1, onUpdate: () => drawBgFill(bg, pair.at(proxy.t), w, h) }, 0)
	}

	/**
	 * The natural exit, at `duration`. `kill()` runs the same settlers for an interrupt.
	 *
	 * ⚠️ The prompt morph is deliberately NOT cleared here. This fires BEFORE `onComplete`, where the thunk's `setLiveObject` snaps `live.diffusion.prompt` to its plain destination — clearing against the stale envelope drops the field to `plain` mode and flickers the text. The thunk clears it after, so the handoff is atomic; `kill()` covers interrupts.
	 */
	_registerCleanup(child, toState, duration) {
		child.tl.call(
			() => {
				// Teardown is ONE-WAY and GSAP fires it on every crossing of `duration`; a scrub crosses the end as often as the user drags. Only a deliberate finish opens the gate.
				if (!child._settling) return
				// `successor` is null: nothing is taking over on a natural completion. `owns` still resolves against `toState`, which is about to become Redux.
				this._settleScene(child, { owns: toState?.objects ?? null })
			},
			[],
			duration
		)
	}

	/**
	 * Tween props for ONE effect. Generalized from the diffusion-only version: every effect is tweenable, and an effect this does not run over simply does not animate during a preset fade — silently, since nothing errors and the value still lands at the end via the final patch.
	 *
	 * Params that must jump rather than sweep are declared `tween: false` in the contract and skipped here; the lookup is effect-qualified because a bare name crosses effects.
	 *
	 * `carriedMorphs` holds the qualified names that were mid-morph when this transition interrupted the last one — for those, "unchanged" is not a reason to skip (see below).
	 */
	getEffectTweenProps(effect, from, to, liveProps = [], carriedMorphs = EMPTY_MAP) {
		const props = {}
		const allSnap = getSnapConfig(effect)
		const snap = {}

		// ⚠️ ITERATE THE CONTRACT, not `to`. A param at its initial is ABSENT from a sparse endpoint, so a loop over `to`'s keys never visits it and it silently jumps at the finish instead of sweeping. Either endpoint may also be a wholly absent effect, which resolves the same way: every param at its initial.
		const schemaProps = EFFECT_SCHEMAS[effect]?.properties ?? {}
		for (const key in schemaProps) {
			const qualified = `${effect}.${key}`
			if (jumpsRatherThanTweens(effect, key)) continue
			// Skip props marked as live
			if (isPinned(liveProps, qualified)) continue

			const initial = schemaProps[key].initial
			const toVal = to?.[key] ?? initial
			const fromVal = from?.[key] ?? initial

			// No OBJECT value is a tween endpoint. Two unrelated things land here and both must be skipped: a morph envelope mid-interpolation (the running morph owns that param for the fade), and a nested sub-dict like `diffusion.advanced` (no schema entry, so the tweenable guard above lets it through). Feeding either to GSAP tweens toward an object — every numeric op yields NaN and the param blanks.
			if (isMorphEnvelope(toVal) || isMorphEnvelope(fromVal)) continue

			// ⚠️ A carried morph needs BOTH halves. `getActiveState` snapshots a morphing param to its DESTINATION, so a mid-fade reversal arrives `toVal === fromVal` and would be skipped; matching on the name alone instead re-arms a morph with `from === b` that has nowhere to go.
			const morphs = !!schemaProps[key].morph
			if (
				morphs
					? toVal !== fromVal || (carriedMorphs.has(qualified) && carriedMorphs.get(qualified) !== toVal)
					: typeof toVal === 'number' && toVal !== fromVal
			) {
				props[key] = toVal
				// If this property should snap to integers, add it to snap config
				if (allSnap[key]) {
					snap[key] = allSnap[key]
				}
			}
		}

		return { props, snap }
	}
}

/**
 * Delegate every per-fade field to the child at the playhead.
 *
 * ⚠️ A write with no child OPENS one, timeline-less. Writing per-fade state IS the assertion that a fade exists, and dropping it silently strands the value — `kill()` nulls these fields after clearing the children, and `_carriedMorphs` is seeded this way.
 */
for (const key of CHILD_FIELDS) {
	Object.defineProperty(TransitionManager.prototype, key, {
		get() {
			const c = this._child()
			return c ? c[key] : null
		},
		set(v) {
			let c = this._child()
			if (!c) {
				c = makeFadeChild(null)
				this._children = [c]
				this._at = 0
			}
			c[key] = v
		},
	})
}

const transitionManager = new TransitionManager()

// What a loop sample reaches mid-fade when React has no entry yet: the object this fade is creating under `id`.
if (typeof window !== 'undefined')
	window.pixi_fade_owned = id => {
		for (const e of transitionManager._owned?.values() ?? [])
			if ((e.realId ?? e.id) === id && e.origin === 'created' && !e.pixiObj.destroyed) return e.pixiObj
	}

// Dev only — the rig (tests/manual/rig.js) needs the ownership registries, unreachable from the console otherwise. Never in production: it exposes internals and the object graph.
// `TransitionManager` is also a named export so unit tests can instantiate it directly rather than through the singleton.
if (IS_LOCAL && typeof window !== 'undefined') window.transitionManager = transitionManager

export { TransitionManager }
export default transitionManager
